import throttle from "lodash.throttle";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  FileDown,
  Hand,
  MessageCircle,
  Presentation,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CaptureUpdateAction,
  Excalidraw,
  Footer,
  MainMenu,
  reconcileElements,
  restoreElements,
} from "@excalidraw/excalidraw";
import { getSceneVersion } from "@excalidraw/excalidraw/element";
import { isFrameLikeElement } from "@excalidraw/excalidraw/element/typeChecks";
import { orderByFractionalIndex } from "@excalidraw/excalidraw/fractionalIndex";
import {
  bumpVersion,
  mutateElement,
  newElementWith,
} from "@excalidraw/excalidraw/element/mutateElement";
import {
  frameByPosition,
  isWorksheetElement,
  ownerTagOf,
  studentChangeVerdict,
  withOwnerTag,
} from "@canvaschool/contracts";
import { useHandleLibrary } from "@excalidraw/excalidraw/data/library";

import { getPreferredLanguage } from "../../app-language/language-detector";
import { LibraryIndexedDBAdapter } from "../../data/LocalData";
import { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert } from "../components/ErrorAlert";
import { Link } from "../components/Link";
import { Logo } from "../components/Logo";
import { Modal } from "../components/Modal";
import { recordClientEvent } from "../feedback/clientLog";
import { FeedbackModal } from "../feedback/FeedbackModal";
import { useSurveyPrompt } from "../feedback/useSurveyPrompt";
import { PresentationShow } from "../viewer/PresentationShow";
import { canvasPath, classPath, setLeaveGuard, useNavigate } from "../router";

import { createAssetSync } from "./assetAdapter";
import { createCanvasSync, createUuid } from "./canvasStateAdapter";
import { createCollaborationTransport } from "./collaborationTransport";
import { CommentModal } from "./CommentModal";
import { CompletionPanel } from "./CompletionPanel";
import { CoverOverlay, coverModeFor } from "./CoverOverlay";
import { findMyFrame, overviewViewport } from "./frameLayout";
import {
  FRAME_PALETTE,
  addComment,
  canManageComment,
  editComment,
  readFrameMeta,
  removeComment,
  sealFrame,
  setFrameBackground,
  unsealFrame,
  withTeacherDeletionMark,
  withWorksheetTag,
  writeFrameMeta,
} from "./frameMeta";
import { FrameBackgroundButton } from "./FrameBackgroundButton";
import { FramePdfExportModal } from "./FramePdfExportModal";
import { GalleryShowoffControl } from "./GalleryShowoffControl";
import { GroupSealModal } from "./GroupSealModal";
import { HandRaiseTray } from "./HandRaiseTray";
import { countUnconfirmed, RosterPanel } from "./RosterPanel";
import { SealButton } from "./SealButton";
import { SealedFrameBadge } from "./SealedFrameBadge";
import { IMAGE_PLACE_CURSOR, PIXEL_ART_PLACE_CURSOR } from "./pixelArt/icons";
import { PixelArtEditButton } from "./pixelArt/PixelArtEditButton";
import { PixelArtModal } from "./pixelArt/PixelArtModal";
import {
  buildPixelArtFile,
  createPixelArtElement,
  decodePixelArt,
  isPixelArtImage,
  rotatedRightEdge,
} from "./pixelArt/pixelArtScene";
import { PixelArtToolbarButton } from "./pixelArt/PixelArtToolbarButton";
import { PresentationCompletion } from "./PresentationCompletion";
import {
  addSlide,
  canAddSlide,
  deleteSlide,
  ensureFirstSlide,
  isFirstSlide,
  slideFrames,
  swapSlide,
} from "./presentationSlides";
import { PresentationSlideControls } from "./PresentationSlideControls";
import { TeacherBar } from "./TeacherBar";
import { attachTextInputProbe } from "./textInputProbe";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type {
  ExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/excalidraw/element/types";
import type {
  AppState,
  BinaryFiles,
  Collaborator,
  ExcalidrawImperativeAPI,
  ExcalidrawProps,
  NormalizedZoomValue,
  SocketId,
} from "@excalidraw/excalidraw/types";
import type {
  CanvasMode,
  CanvasRecord,
  CanvasState,
  PresentationStudent,
  ClassGalleryPost,
  Completion,
  LessonState,
  PresenceIdentity,
  PresenceMember,
  SerializedExcalidrawElement,
  StudentRefusal,
} from "@canvaschool/contracts";
import type { ApiErrorCode } from "../api/client";
import type { FeedbackSource } from "../feedback/FeedbackModal";
import type { EditorRoute } from "../router";
import type { AssetSync, AssetSyncState } from "./assetAdapter";
import type { CanvasSync, SaveStatus, SyncNotice } from "./canvasStateAdapter";
import type {
  CollaborationTransport,
  TransportFactory,
} from "./collaborationTransport";
import type { PixelArtFile } from "./pixelArt/pixelArtScene";
import type { PixelGrid } from "./pixelArt/pixelGrid";

const POINTER_INTERVAL_MILLISECONDS = 50;

/** A raised hand nobody lowers is a stuck signal on the teacher's screen;
 * this is what makes it self-heal on the student's own device. */
const HAND_RAISE_TIMEOUT_MILLISECONDS = 10 * 60 * 1_000;
/** The largest delay `window.setTimeout` accepts (a 32-bit signed int of
 * milliseconds); a longer requested delay silently fires immediately
 * otherwise. Same clamp as `AuthProvider`'s session-refresh timer. */
const MAX_TIMER_DELAY_MILLISECONDS = 2_147_483_647;

const chimeMutedStorageKey = (classId: string) =>
  `canvaschool:chimeMuted:${classId}`;

/** Module-level, not state: the AudioContext must survive remounts of any
 * one canvas, and there is exactly one chime for the whole tab. */
const chime = { context: null as AudioContext | null, muted: false };

/** Browsers block audio until the page has been clicked once; call this
 * from any teacher-only click so the chime is ready by the time it matters.
 * `AudioContext` doesn't exist in jsdom (or very old browsers), so a click
 * anywhere in the teacher controls — including the new checkpoint button —
 * must not throw when it's missing. */
export const primeChime = () => {
  if (typeof AudioContext === "undefined") {
    return;
  }
  chime.context ??= new AudioContext();
  void chime.context.resume();
};

/** The teacher's own on/off switch — thirty tablets chiming at once is a
 * riot, but one teacher choosing silence is not a reason to disable it. */
export const setChimeMuted = (muted: boolean) => {
  chime.muted = muted;
};

const playChime = () => {
  if (chime.muted || !chime.context) {
    return;
  }
  const oscillator = chime.context.createOscillator();
  oscillator.frequency.value = 880;
  oscillator.connect(chime.context.destination);
  oscillator.start();
  oscillator.stop(chime.context.currentTime + 0.18);
};

/** Every student sees the same scale, so artwork sizes stay comparable.
 * Set to 75% so that the 840x594 frame fits comfortably within the
 * browser viewport without being clipped by browser/editor chrome.
 */
const STANDARD_ZOOM_VALUE = 0.75 as AppState["zoom"]["value"];

/** Opens the teacher on every frame at once; see `overviewViewport`.
 * False when the scene has no frames yet. */
const showOverview = (api: ExcalidrawImperativeAPI): boolean => {
  const view = overviewViewport(api.getSceneElements(), api.getAppState());
  if (!view) {
    return false;
  }
  api.updateScene({
    appState: {
      scrollX: view.scrollX,
      scrollY: view.scrollY,
      zoom: { value: view.zoom as NormalizedZoomValue },
    },
    captureUpdate: CaptureUpdateAction.NEVER,
  });
  return true;
};

/** Ctrl/Cmd + these keys change zoom in Excalidraw's own shortcuts. */
const ZOOM_KEY_CODES = new Set([
  "Equal",
  "Minus",
  "Digit0",
  "NumpadAdd",
  "NumpadSubtract",
  "Numpad0",
]);

const NOTICES: Record<SyncNotice, string> = {
  EPOCH_DISCARDED: "복원으로 저장되지 않은 변경이 취소되었습니다",
  CHANGES_REJECTED: "일부 변경을 저장할 수 없어 취소했습니다.",
  READ_ONLY_DISCARDED:
    "학급이 보관되어 저장되지 않은 변경을 저장할 수 없습니다.",
};

const LEAVE_WARNING =
  "저장하지 않은 변경 내용이 있습니다. 지금 나가면 사라집니다.";

type FrameDeleteRequest = {
  frames: Parameters<NonNullable<ExcalidrawProps["confirmFrameDelete"]>>[0];
  answer: (confirmed: boolean) => void;
};

/** Shown once the core finishes processing a picked image and is waiting
 * for a placement click. Local processing (resize/hash/decode) before this
 * point only changes the native cursor, which reads as a stuck loader. */
const FIRST_SLIDE_KEPT_NOTICE = "첫 슬라이드는 지울 수 없어요";
const IMAGE_PLACEMENT_HINT = "이미지를 넣고 싶은 곳을 클릭하세요";
const PIXEL_ART_PLACEMENT_HINT = "픽셀 아트를 넣고 싶은 곳을 클릭하세요";
const PIXEL_ART_STALE_NOTICE =
  "지금은 이 그림을 고칠 수 없어요. 고친 그림은 넣지 못했어요.";

/** A picked-but-unplaced image or pixel art: the cursor and hint shown until
 * the placement click. */
type Placing = "pixel" | "image" | null;

type PixelArtModalState = { initialGrid?: PixelGrid; editId?: string };

/** HTTP only returns the scene; the socket join fills this in for real.
 * Comments start locked: an open thread on a young student's sealed work
 * invites exactly the unkind one-liner a teacher does not want to referee
 * mid-class, and the brief pre-join window should default to caution. */
const UNLOCKED_LESSON = (canvasId: string): LessonState => ({
  canvasId,
  lock: { mode: "OFF", message: "", endsAt: null, exemptStudentNumbers: [] },
  completion: { mode: "GROUP", checked: [] },
  groupCount: 6,
  studentComments: false,
});

/** Codes that end the session rather than this canvas. */
const SESSION_CODES = new Set<ApiErrorCode>([
  "UNAUTHENTICATED",
  "ACCOUNT_DISABLED",
]);

const labelFor = (identity: PresenceIdentity) =>
  identity.role === "STUDENT"
    ? `${identity.studentNumber}번 ${identity.displayName}`
    : identity.displayName;

/** Why a student's own edit snapped back (spec 2026-09-28 §7). A frame's
 * structure is never theirs to change, and saying so on every stray click
 * would only nag, so that one stays silent. */
const REFUSAL_MESSAGES: Partial<Record<StudentRefusal, string>> = {
  sealed: "마감된 프레임이에요. 고치려면 먼저 마감을 풀어요.",
  outside: "내 프레임 안에서만 그리고 옮길 수 있어요.",
  notMine: "친구 것은 고칠 수 없어요.",
  teacher: "선생님이 놓은 것은 고칠 수 없어요.",
};
const REFUSAL_NOTICE_INTERVAL_MS = 10_000;

/** Screen pixels above a slide that still count as pointing at it, so the
 * pointer can travel up onto its button row. */
const SLIDE_HOVER_MARGIN_PX = 48;

const AUTO_SHOW_KEY = "canvaschool:presentation:autoShow";

/** A teacher who steps on from a slideshow opens the next student's canvas
 * and lands in its slideshow; this note carries that across the navigation. */
const writeAutoShow = (canvasId: string) => {
  try {
    window.sessionStorage.setItem(AUTO_SHOW_KEY, canvasId);
  } catch {
    // Without storage the teacher just lands in the canvas.
  }
};

const readAutoShow = (canvasId: string): boolean => {
  try {
    const wanted = window.sessionStorage.getItem(AUTO_SHOW_KEY) === canvasId;
    window.sessionStorage.removeItem(AUTO_SHOW_KEY);
    return wanted;
  } catch {
    return false;
  }
};

/** A student's new image is scaled to fit this share of their frame. */
const IMAGE_FIT_RATIO = 0.9;

const serializedOf = (element: ExcalidrawElement) =>
  element as unknown as SerializedExcalidrawElement;

/** Whether the student could swap this pixel art's picture: judged by the
 * shared rule on a real change (a different `fileId`), so ownership, a seal
 * and worksheet material are all decided exactly as for any other edit. */
const studentMayReplacePixelArt = (
  elements: readonly ExcalidrawElement[],
  studentNumber: number | null,
  element: ExcalidrawElement,
  presentation = false,
) =>
  studentNumber !== null &&
  studentChangeVerdict(elements.map(serializedOf), studentNumber, {
    presentation,
  })(
    serializedOf(element),
    serializedOf(newElementWith(element, { fileId: "check" } as any)),
  ) === null;

/** Nothing is mid-gesture: no shape being drawn, dragged, resized, rotated,
 * typed into or waiting for its placement click. */
const isAtRest = (appState: AppState) =>
  !appState.newElement &&
  !appState.multiElement &&
  !appState.editingTextElement &&
  !appState.selectedElementsAreBeingDragged &&
  !appState.isResizing &&
  !appState.isRotating &&
  !appState.pendingImageElementId;

/**
 * A student never edits a frame, yet a box selection that encloses one (or
 * Ctrl+A) selects the frame instead of what is drawn in it, so the drag or
 * delete that follows snapped back as a refused frame edit (live class,
 * 10-08). Swaps each selected frame for its own contents. Returns null when
 * the selection holds no frame.
 *
 * Only a box drag (`boxDragging`) earns the swap: there the student did
 * enclose those drawings. A plain click on a frame's border or name, and
 * Ctrl+A (which already selects the drawings themselves), only lose the
 * frame — otherwise one stray click selected a whole frame's work and the
 * next Delete wiped it (live class, 10-13).
 */
const withoutSelectedFrames = (
  elements: readonly ExcalidrawElement[],
  selected: AppState["selectedElementIds"],
  boxDragging: boolean,
): AppState["selectedElementIds"] | null => {
  const frameIds = new Set(
    elements
      .filter((element) => isFrameLikeElement(element) && selected[element.id])
      .map((element) => element.id),
  );
  if (frameIds.size === 0) {
    return null;
  }
  const next: Record<string, true> = {};
  for (const element of elements) {
    if (element.isDeleted || frameIds.has(element.id)) {
      continue;
    }
    const inSelectedFrame =
      boxDragging &&
      element.frameId !== null &&
      frameIds.has(element.frameId) &&
      !element.locked &&
      !("containerId" in element && element.containerId);
    if (selected[element.id] || inSelectedFrame) {
      next[element.id] = true;
    }
  }
  return next;
};

/**
 * Settles a student's own edits before they are judged and sent (spec
 * 2026-09-28): tags what they create with their number (in place, as the
 * core creates it — see the teacher's worksheet tag below for why), and,
 * once the gesture is over, scales a new image into their frame and writes
 * `frameId` from where each edited element actually sits. The core picks
 * `frameId` from the *pointer* at the end of a drag, so without this an
 * element could claim a frame it is not in and be clipped out of sight.
 * Returns true when a settled change needs a re-render (and a resend).
 */
const settleStudentEdits = (
  elements: readonly ExcalidrawElement[],
  appState: AppState,
  sync: CanvasSync,
  selfNumber: number,
): boolean => {
  const frames = elements.filter(
    (element) => isFrameLikeElement(element) && !element.isDeleted,
  );
  const myFrame = frames.find(
    (frame) => readFrameMeta(frame).owner === selfNumber,
  );
  const atRest = isAtRest(appState);
  let settled = false;
  const changed = new Set<string>();
  for (const element of elements) {
    if (isFrameLikeElement(element) || element.isDeleted) {
      continue;
    }
    const stored = sync.revisionOf(element.id);
    if (!stored && ownerTagOf(serializedOf(element)) !== selfNumber) {
      mutateElement(
        element,
        {
          customData: withOwnerTag(serializedOf(element), selfNumber)
            .customData as ExcalidrawElement["customData"],
        },
        false,
      );
    }
    if (!atRest || (stored && stored.version === element.version)) {
      continue;
    }
    if (
      !stored &&
      myFrame &&
      element.type === "image" &&
      element.width > 0 &&
      element.height > 0
    ) {
      const scale = Math.min(
        1,
        (myFrame.width * IMAGE_FIT_RATIO) / element.width,
        (myFrame.height * IMAGE_FIT_RATIO) / element.height,
      );
      const width = element.width * scale;
      const height = element.height * scale;
      const clamp = (value: number, low: number, high: number) =>
        Math.min(Math.max(value, low), high);
      const centerX = clamp(
        element.x + element.width / 2,
        myFrame.x + width / 2,
        myFrame.x + myFrame.width - width / 2,
      );
      const centerY = clamp(
        element.y + element.height / 2,
        myFrame.y + height / 2,
        myFrame.y + myFrame.height - height / 2,
      );
      const x = centerX - width / 2;
      const y = centerY - height / 2;
      if (
        width !== element.width ||
        height !== element.height ||
        x !== element.x ||
        y !== element.y
      ) {
        mutateElement(element, { x, y, width, height }, false);
        settled = true;
      }
    }
    changed.add(element.id);
  }
  return (
    settleFrameIds(elements, frames, (element) => changed.has(element.id)) ||
    settled
  );
};

/**
 * Writes `frameId` from where each chosen element actually sits — the frame
 * its center lies in, or none — and makes a label follow its container.
 * Returns true when anything changed.
 */
const settleFrameIds = (
  elements: readonly ExcalidrawElement[],
  frames: readonly ExcalidrawElement[],
  chosen: (element: ExcalidrawElement) => boolean,
): boolean => {
  let settled = false;
  const expectedFrameIds = new Map<string, string | null>();
  for (const element of elements) {
    if (
      isFrameLikeElement(element) ||
      element.isDeleted ||
      ("containerId" in element && element.containerId) ||
      !chosen(element)
    ) {
      continue;
    }
    const expected =
      frameByPosition(serializedOf(element), frames.map(serializedOf))?.id ??
      null;
    expectedFrameIds.set(element.id, expected);
    if ((element.frameId ?? null) !== expected) {
      mutateElement(element, { frameId: expected }, false);
      settled = true;
    }
  }
  // A label follows its container.
  for (const element of elements) {
    if (
      element.isDeleted ||
      !("containerId" in element) ||
      !element.containerId ||
      !expectedFrameIds.has(element.containerId)
    ) {
      continue;
    }
    const expected = expectedFrameIds.get(element.containerId) ?? null;
    if ((element.frameId ?? null) !== expected) {
      mutateElement(element, { frameId: expected }, false);
      settled = true;
    }
  }
  return settled;
};

/**
 * The teacher's counterpart, with no timer: when the teacher's own edit
 * moves a student's element, or moves, resizes or deletes a frame, the
 * student elements involved get `frameId` from their position in that same
 * edit. The core already re-parents on its own frame gestures; this also
 * covers the pointer-picked `frameId` of a dragged element and anything the
 * core leaves behind. Teacher material keeps the core's own membership — a
 * worksheet image may span several frames on purpose.
 */
const settleTeacherEdits = (
  elements: readonly ExcalidrawElement[],
  appState: AppState,
  sync: CanvasSync,
): boolean => {
  if (!isAtRest(appState)) {
    return false;
  }
  const isChanged = (element: ExcalidrawElement) => {
    const stored = sync.revisionOf(element.id);
    return !stored || stored.version !== element.version;
  };
  const frames = elements.filter(
    (element) => isFrameLikeElement(element) && !element.isDeleted,
  );
  const liveFrameIds = new Set(frames.map((frame) => frame.id));
  const changedFrames = elements.filter(
    (element) => isFrameLikeElement(element) && isChanged(element),
  );
  const changedFrameIds = new Set(changedFrames.map((frame) => frame.id));
  const liveChangedFrames = changedFrames
    .filter((frame) => !frame.isDeleted)
    .map(serializedOf);
  return settleFrameIds(elements, frames, (element) => {
    if (isWorksheetElement(serializedOf(element))) {
      return false;
    }
    if (isChanged(element)) {
      return true;
    }
    if (element.frameId) {
      return (
        changedFrameIds.has(element.frameId) ||
        !liveFrameIds.has(element.frameId)
      );
    }
    return (
      liveChangedFrames.length > 0 &&
      frameByPosition(serializedOf(element), liveChangedFrames) !== null
    );
  });
};

const FOCUS_MODE_INDIVIDUAL_ONLY =
  "집중모드는 개인 템플릿에서만 사용할 수 있습니다.";

const toScene = (elements: readonly ExcalidrawElement[]) =>
  elements as unknown as readonly SerializedExcalidrawElement[];

/**
 * Canonical elements as the editor needs them. `restoreElements` fills
 * defaults, but it also *edits*: it deletes an empty text element (a peer's
 * text box before its first keystroke) and re-numbers invalid indices, each
 * with a version bump. Arriving that way, a peer's element would read as a
 * local edit and be sent back — the whole class deleted a student's text
 * while they were still typing it (chaos run, seed 2). Remote elements keep
 * the server's deletion state and revision; index repairs are settled as
 * bookkeeping by the adapter.
 */
const fromScene = (elements: readonly SerializedExcalidrawElement[]) => {
  const canonical = new Map(elements.map((element) => [element.id, element]));
  return restoreElements(elements as unknown as ExcalidrawElement[], null).map(
    (element) => {
      const stored = canonical.get(element.id);
      if (
        !stored ||
        (stored.isDeleted === element.isDeleted &&
          stored.version === element.version &&
          stored.versionNonce === element.versionNonce)
      ) {
        return element;
      }
      return {
        ...element,
        isDeleted: stored.isDeleted,
        version: stored.version,
        versionNonce: stored.versionNonce,
      } as typeof element;
    },
  );
};

export type CanvasEditorProps = {
  route: EditorRoute;
  /** The canvas record when the caller already has it; read here otherwise. */
  canvas?: CanvasRecord;
  createTransport?: TransportFactory;
};

type PointerPayload = Parameters<
  NonNullable<ExcalidrawProps["onPointerUpdate"]>
>[0];

const EditorSurface = ({
  route,
  initial,
  canvas,
  createTransport,
}: {
  route: EditorRoute;
  initial: CanvasState;
  canvas: CanvasRecord;
  createTransport: TransportFactory;
}) => {
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;
  const { api } = auth;
  const { classId, canvasId } = route;
  const navigate = useNavigate();
  // A student's slides inside a presentation lesson (발표 캔버스): the class
  // lesson machinery (hand raise, lock/cover, completion strip, focus mode,
  // roster, seals, gallery) does not apply, and the student rule is the
  // presentation one — on the client and the server alike.
  const isPresentation = canvas.kind === "PRESENTATION_STUDENT";

  const zoomLocked =
    auth.state.status === "authenticated" &&
    auth.state.session.principal.role === "STUDENT";

  const isTeacher =
    auth.state.status === "authenticated" &&
    auth.state.session.principal.role === "TEACHER";

  // Excalidraw's default file I/O (open, save to file, export JSON, save as
  // image) is closed for every role: Canvaschool keeps its own copy of each
  // canvas, and a file loaded back in would bypass class permissions and
  // persistence. The teacher gets "PDF로 내보내기" in the main menu instead.
  // Only students additionally lose the frame tool. The core's "캔버스
  // 초기화" (menu and command palette) is the teacher's alone, and not on a
  // presentation canvas: nothing there refuses it, and it wiped a student's
  // every slide at once (10-30).
  const uiOptions = useMemo<ExcalidrawProps["UIOptions"]>(
    () => ({
      canvasActions: {
        export: false,
        saveAsImage: false,
        saveToActiveFile: false,
        loadScene: false,
        ...(isTeacher && !isPresentation ? {} : { clearCanvas: false }),
      },
      ...(isTeacher ? {} : { tools: { frame: false } }),
      // Phones get the viewer instead, so whatever opens the editor is big
      // enough for the desktop layout, and only that layout has the footer
      // the lesson dock lives in: a tablet in portrait fell below the core's
      // mobile breakpoint and lost hand raise and the completion check.
      forceDesktopUI: true,
    }),
    [isTeacher, isPresentation],
  );

  // A teacher erasing inside a student's frame brushed its border and the
  // eraser took the frame and all its drawing (live class, 10-23); not every
  // teacher knows Ctrl+Z. The core now keeps the eraser off frames, and a
  // delete that includes one waits for this question: it leaves the drawing
  // but takes the student's place to draw.
  const [frameDelete, setFrameDelete] = useState<FrameDeleteRequest | null>(
    null,
  );
  const confirmFrameDelete = useCallback<
    NonNullable<ExcalidrawProps["confirmFrameDelete"]>
  >(
    (frames) => {
      // The first slide is never deleted (see `isPresentationFirstSlide`);
      // the server refuses a student's attempt, so nobody is asked.
      if (isPresentation && frames.some((frame) => isFirstSlide(frame))) {
        setNotice(FIRST_SLIDE_KEPT_NOTICE);
        return Promise.resolve(false);
      }
      return new Promise((resolve) =>
        setFrameDelete({ frames, answer: resolve }),
      );
    },
    [isPresentation],
  );
  const answerFrameDelete = (confirmed: boolean) => {
    frameDelete?.answer(confirmed);
    setFrameDelete(null);
  };

  /** Null for a teacher: only a student's own screen can be covered. */
  const selfNumber =
    auth.state.status === "authenticated" &&
    auth.state.session.principal.role === "STUDENT"
      ? auth.state.session.studentProfile?.studentNumber ?? null
      : null;

  const [excalidrawAPI, setExcalidrawAPI] =
    useState<ExcalidrawImperativeAPI | null>(null);
  const [mode, setMode] = useState<CanvasMode>(initial.mode);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>(
    initial.mode === "READ_ONLY" ? "readOnly" : "saved",
  );
  const [connected, setConnected] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<FeedbackSource | null>(null);
  const closeFeedback = useCallback(() => setFeedback(null), []);
  // Teachers are asked too, for now (open beta).
  const [surveyDue, dismissSurvey] = useSurveyPrompt(
    auth.state.status === "authenticated"
      ? auth.state.session.principal.userId
      : null,
  );
  const [showScene, setShowScene] = useState<{
    elements: readonly ExcalidrawElement[];
    files: BinaryFiles;
  } | null>(null);
  const openShowRef = useRef<() => void>(() => undefined);
  const renderTopRightUI = useCallback(
    // On a phone the slot shares the toolbar's row: icon only there.
    (isMobile: boolean) => (
      <>
        {isPresentation && (
          <button
            type="button"
            className="canvaschool-report-button canvaschool-present-button"
            aria-label="발표"
            title="전체 화면으로 발표하기"
            onClick={() => openShowRef.current()}
          >
            <Presentation size={16} aria-hidden="true" />
            {!isMobile && "발표"}
          </button>
        )}
        <button
          type="button"
          className="canvaschool-report-button"
          aria-label="의견·요청"
          title="의견·요청 보내기"
          onClick={() => setFeedback("USER_REPORT")}
        >
          <MessageCircle size={16} aria-hidden="true" />
          {!isMobile && "의견·요청"}
        </button>
      </>
    ),
    [isPresentation],
  );
  const [leaveOpen, setLeaveOpen] = useState(false);
  // HTTP load has no lesson state; the socket join fills in the real one.
  const [lesson, setLesson] = useState<LessonState>(UNLOCKED_LESSON(canvasId));
  const [roster, setRoster] = useState<readonly PresenceMember[]>([]);
  const [handRaised, setHandRaised] = useState(false);
  const [raisedAt, setRaisedAt] = useState<string | null>(null);
  // Bumped on every onChange so memos reading the imperative API (which
  // holds no state of its own) know to recompute.
  const [sceneTick, setSceneTick] = useState(0);
  // Set for real once the join tells us our own presence identity; the
  // fallback covers the brief window before that.
  const [selfLabel, setSelfLabel] = useState(isTeacher ? "선생님" : "학생");
  // Screen coordinates for comment panes, kept alongside the zoom-lock use
  // of onScrollChange rather than replacing it.
  const [viewport, setViewport] = useState({
    scrollX: 0,
    scrollY: 0,
    zoom: 1,
  });
  const leaveProceed = useRef<(() => void) | null>(null);
  const [accessCode, setAccessCode] = useState<ApiErrorCode | null>(null);
  const [assets, setAssets] = useState<AssetSyncState>({
    uploading: 0,
    failedUploads: 0,
    failedDownloads: 0,
  });
  const syncRef = useRef<CanvasSync | null>(null);
  const assetRef = useRef<AssetSync | null>(null);
  const transportRef = useRef<CollaborationTransport | null>(null);
  const [initialElements] = useState(() => fromScene(initial.scene));
  // A canvas remount means a new entry and gets a fresh jump. Reconnects
  // within the same editor must not drag a student away from active work.
  const hasEnteredOwnFrame = useRef(false);
  // Same once-per-entry rule for the teacher's whole-class overview.
  const hasShownOverview = useRef(false);
  // Tracks the core's own pending-image-placement state so we only touch
  // the toast on a real transition, not on every unrelated scene change.
  const pendingImageElementId = useRef<string | null>(null);
  // Pixel art: the PNG waits here between the modal's save and the placement
  // click. `armed` flips once the core is seen in the pixel tool, so a later
  // exit from it (Esc, another tool) reads as a cancel.
  const [placing, setPlacing] = useState<Placing>(null);
  const placingRef = useRef<Placing>(null);
  const [pixelModal, setPixelModal] = useState<PixelArtModalState | null>(null);
  const pixelModalRef = useRef<PixelArtModalState | null>(null);
  pixelModalRef.current = pixelModal;
  const pendingPixelArtRef = useRef<PixelArtFile | null>(null);
  const pixelArmedRef = useRef(false);
  const readOnlyRef = useRef(false);

  useHandleLibrary({ excalidrawAPI, adapter: LibraryIndexedDBAdapter });

  // The opening scene is not handed to the core as initialData: the core
  // runs initialData through its own restoreElements, whose edits (an empty
  // text box deleted, with a version bump) then read as the user's own and
  // were sent — reopening a canvas deleted a classmate's text box (chaos
  // run, seed 4). It goes in through updateScene once the core has loaded;
  // see `onChange`.
  const initialSceneApplied = useRef(false);
  const [initialData] = useState(() => ({
    elements: [],
    scrollToContent: true,
    appState: {
      // Canvaschool always keeps the drawn tool active instead of
      // reverting to selection after each shape (core default is
      // unlocked); see `restoreAppState` — it reads this whole object
      // as-is, so `type`/`customType` must be supplied alongside `locked`.
      activeTool: {
        type: "selection" as const,
        customType: null,
        locked: true,
        lastActiveTool: null,
      },
      ...(zoomLocked ? { zoom: { value: STANDARD_ZOOM_VALUE } } : {}),
    },
  }));
  const [langCode] = useState(getPreferredLanguage);
  const canvasWrapperRef = useRef<HTMLDivElement | null>(null);

  // Ctrl/Cmd+S, Ctrl/Cmd+Shift+S, Ctrl/Cmd+O and Ctrl/Cmd+Shift+E would
  // otherwise reach Excalidraw's save / open / export-image actions even
  // though their menu items are hidden. Capture phase on `document` so this
  // runs before Excalidraw's own document listener.
  useEffect(() => {
    const blockFileShortcuts = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey) {
        return;
      }
      // Excalidraw matches on `event.key`, others (layouts, IME) arrive only
      // with `event.code`; either one is enough to block.
      const key = (event.key ?? "").toLowerCase();
      if (
        event.code === "KeyS" ||
        key === "s" ||
        event.code === "KeyO" ||
        key === "o" ||
        (event.shiftKey && (event.code === "KeyE" || key === "e"))
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener("keydown", blockFileShortcuts, {
      capture: true,
    });
    return () => {
      document.removeEventListener("keydown", blockFileShortcuts, {
        capture: true,
      });
    };
  }, []);

  // Dropping a scene file (.excalidraw / JSON) on the canvas makes Excalidraw
  // replace the whole scene, which would bypass the hidden "Open" item and the
  // class's persistence. Stop it before it reaches Excalidraw's container;
  // image drops are left alone. Applies to every role.
  useEffect(() => {
    const wrapper = canvasWrapperRef.current;
    if (!wrapper) {
      return;
    }
    const blockSceneDrop = (event: DragEvent) => {
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (
        files.some(
          (file) =>
            /\.(excalidraw|json)$/i.test(file.name) ||
            file.type === "application/json" ||
            file.type === "application/vnd.excalidraw+json",
        )
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    wrapper.addEventListener("drop", blockSceneDrop, { capture: true });
    return () => {
      wrapper.removeEventListener("drop", blockSceneDrop, { capture: true });
    };
  }, []);

  useEffect(() => {
    if (!zoomLocked) {
      return;
    }
    const wrapper = canvasWrapperRef.current;
    if (!wrapper) {
      return;
    }

    // Ctrl/Cmd+wheel is how mouse-wheel and trackpad-pinch zoom reach
    // Excalidraw; stopping it here (before it reaches the container
    // Excalidraw itself listens on) leaves plain-wheel panning untouched.
    const blockZoomWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    // Excalidraw's zoom shortcuts listen on `document`, so the blocker
    // has to sit there too, in the capture phase, to run first. They also
    // fire on Shift alone (no Ctrl/Cmd), so that has to be blocked as well.
    const blockZoomKeys = (event: KeyboardEvent) => {
      if (
        (event.ctrlKey || event.metaKey || event.shiftKey) &&
        ZOOM_KEY_CODES.has(event.code)
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    // Safari reports trackpad pinch as gesture events instead of wheel.
    const blockGesture = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    wrapper.addEventListener("wheel", blockZoomWheel, {
      capture: true,
      passive: false,
    });
    wrapper.addEventListener("gesturestart", blockGesture, { capture: true });
    wrapper.addEventListener("gesturechange", blockGesture, {
      capture: true,
    });
    document.addEventListener("keydown", blockZoomKeys, { capture: true });

    return () => {
      wrapper.removeEventListener("wheel", blockZoomWheel, { capture: true });
      wrapper.removeEventListener("gesturestart", blockGesture, {
        capture: true,
      });
      wrapper.removeEventListener("gesturechange", blockGesture, {
        capture: true,
      });
      document.removeEventListener("keydown", blockZoomKeys, {
        capture: true,
      });
    };
  }, [zoomLocked]);

  // Excalidraw only handles wheel events from inside its own container, so
  // Ctrl/Cmd+wheel over our overlays (seal badges, which keep a fixed size
  // and pile up over the frames when zoomed far out) went to the browser and
  // zoomed the page UI instead of the canvas. Block that and hand the event
  // to the canvas, which zooms around the pointer it tracks on `document`.
  useEffect(() => {
    if (zoomLocked) {
      return;
    }
    const wrapper = canvasWrapperRef.current;
    if (!wrapper) {
      return;
    }
    const forwardZoomWheel = (event: WheelEvent) => {
      const target = event.target as Element | null;
      if (
        !(event.ctrlKey || event.metaKey) ||
        target?.closest(".excalidraw-container")
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (target?.closest('[role="dialog"], [aria-modal="true"]')) {
        return;
      }
      wrapper
        .querySelector("canvas.excalidraw__canvas.interactive")
        ?.dispatchEvent(
          new WheelEvent("wheel", {
            bubbles: true,
            cancelable: true,
            clientX: event.clientX,
            clientY: event.clientY,
            deltaX: event.deltaX,
            deltaY: event.deltaY,
            deltaMode: event.deltaMode,
            ctrlKey: event.ctrlKey,
            metaKey: event.metaKey,
          }),
        );
    };
    wrapper.addEventListener("wheel", forwardZoomWheel, {
      capture: true,
      passive: false,
    });
    return () => {
      wrapper.removeEventListener("wheel", forwardZoomWheel, { capture: true });
    };
  }, [zoomLocked]);

  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    let collaborators = new Map<SocketId, Collaborator>();
    let selfConnectionId: string | null = null;
    let transport: CollaborationTransport | null = null;

    const showCollaborators = (next: Map<SocketId, Collaborator>) => {
      collaborators = next;
      excalidrawAPI.updateScene({ collaborators: next });
    };

    const applyPresence = (members: readonly PresenceMember[]) => {
      const next = new Map<SocketId, Collaborator>();
      for (const member of members) {
        const id = member.identity.connectionId as SocketId;
        next.set(id, {
          ...collaborators.get(id),
          id,
          socketId: id,
          username: labelFor(member.identity),
          isCurrentUser: member.identity.connectionId === selfConnectionId,
        });
      }
      setRoster(members);
      showCollaborators(next);
    };

    const editableScene = () => {
      const { pendingImageElementId } = excalidrawAPI.getAppState();
      const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
      return toScene(
        pendingImageElementId
          ? elements.filter((element) => element.id !== pendingImageElementId)
          : elements,
      );
    };
    const assetSync = createAssetSync({
      api,
      classId,
      canvasId,
      excalidrawAPI,
      savedFileIds: initial.assetIds,
      onChange: setAssets,
      // Never the image still awaiting its placement click: a file already
      // stored (the same picture picked again) reports saved at once, and
      // judging the unplaced image then snapped it away from under the
      // core, which dropped back to the selection tool (live class, 09-28).
      onFileSaved: () => sync.localChange(editableScene()),
    });

    const loseAccess = (code: ApiErrorCode) => {
      setAccessCode(code);
      sync.revoke(
        code === "NETWORK_FAILED" || code === "INVALID_RESPONSE"
          ? "PERSISTENCE_FAILED"
          : code,
      );
      if (SESSION_CODES.has(code)) {
        void authRef.current.reloadSession({ background: true });
      }
    };

    const lastRefusalNotice = new Map<StudentRefusal, number>();

    const sync = createCanvasSync({
      classId,
      canvasId,
      clientId: createUuid(),
      initial,
      scene: {
        getElements: () =>
          toScene(excalidrawAPI.getSceneElementsIncludingDeleted()),
        getEditable: editableScene,
        restore: (elements, placeByIndex = false) => {
          // Exactly the revisions the server holds, not run through
          // `restoreElements`: its normalizing (filling defaults, deleting
          // empty text) would make the restored copy a fresh edit, which a
          // refused student edit would then refuse again, forever. Cloned
          // because the core mutates scene elements in place.
          const byId = new Map(
            elements.map((element) => [
              element.id,
              structuredClone(element) as unknown as ExcalidrawElement,
            ]),
          );
          const restored = excalidrawAPI
            .getSceneElementsIncludingDeleted()
            .map((element) => byId.get(element.id) ?? element);
          excalidrawAPI.updateScene({
            elements: placeByIndex
              ? orderByFractionalIndex(restored as OrderedExcalidrawElement[])
              : restored,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          setSceneTick((value) => value + 1);
        },
        replace: (elements) => {
          assetSync.noteCanonical(elements);
          excalidrawAPI.updateScene({
            elements: fromScene(elements),
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          // A whole-scene replace only ever happens on an epoch change: a
          // restored revision, or the forced resync after a verified scene
          // mismatch (H8). Either way, the undo stack still holds entries
          // from the *previous* epoch; letting a teacher undo into them
          // would replay edits the current epoch never had.
          excalidrawAPI.history.clear();
          setSceneTick((value) => value + 1);
        },
        adoptRevisions: (revisions) => {
          const byId = new Map(revisions.map((entry) => [entry.id, entry]));
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          let moved = false;
          for (const element of elements) {
            const revision = byId.get(element.id);
            if (revision) {
              // In place, as the core's own index repair was: a revision
              // correction, not a scene change to re-render or re-send.
              Object.assign(element, {
                version: revision.version,
                versionNonce: revision.versionNonce,
                updated: revision.updated,
                ...(revision.index === undefined
                  ? {}
                  : { index: revision.index }),
              });
              moved ||= revision.index !== undefined;
            }
          }
          if (moved) {
            // The server's index back in place of this screen's own repair:
            // the element also has to sit where that index says.
            excalidrawAPI.updateScene({
              elements: orderByFractionalIndex([
                ...elements,
              ] as OrderedExcalidrawElement[]),
              captureUpdate: CaptureUpdateAction.NEVER,
            });
            setSceneTick((value) => value + 1);
          }
        },
        reconcile: (elements) => {
          assetSync.noteCanonical(elements);
          const reconciled = reconcileElements(
            excalidrawAPI.getSceneElementsIncludingDeleted(),
            fromScene(elements) as RemoteExcalidrawElement[],
            excalidrawAPI.getAppState(),
          );
          excalidrawAPI.updateScene({
            elements: reconciled,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          setSceneTick((value) => value + 1);
        },
      },
      send: (mutation) =>
        transport
          ? transport.sendMutation(mutation)
          : Promise.reject(new ApiError(0, "NETWORK_FAILED")),
      isFileReady: (fileId) => assetSync.isFileReady(fileId),
      student: isTeacher
        ? undefined
        : { studentNumber: selfNumber, presentation: isPresentation },
      onRefused: (reason) => {
        const message = REFUSAL_MESSAGES[reason];
        const at = Date.now();
        if (
          !message ||
          at - (lastRefusalNotice.get(reason) ?? -Infinity) <
            REFUSAL_NOTICE_INTERVAL_MS
        ) {
          return;
        }
        lastRefusalNotice.set(reason, at);
        excalidrawAPI.setToast({ message, duration: 3_000 });
      },
      onStatus: setSaveStatus,
      onNotice: (next) => setNotice(NOTICES[next]),
      onAccessLost: (code) => loseAccess(code),
      onReauthenticate: () => transport?.reconnect(),
      onResyncRequired: () => transport?.reconnect(),
      // Operator diagnostic only: lets logs tell a dropped socket apart from
      // a client stuck behind on version while a save is pending.
      onDiagnostic: (reason) =>
        console.info("[canvaschool] save pending", { reason }),
      // Figma-style divergence detection; harmless against a server that
      // doesn't answer it yet (see `CollaborationTransport.verify`).
      verify: (verifyCanvasId) =>
        transport
          ? transport.verify(verifyCanvasId)
          : Promise.reject(new ApiError(0, "NETWORK_FAILED")),
      repair: (request) =>
        transport
          ? transport.repair(request)
          : Promise.reject(new ApiError(0, "NETWORK_FAILED")),
      diagnostic: (kind, detail) => {
        recordClientEvent("diagnostic", `${kind} ${JSON.stringify(detail)}`);
        transport?.diagnostic({ canvasId, kind, detail });
      },
    });

    transport = createTransport({
      classId,
      canvasId,
      getCsrfToken: async () => {
        const session = await api.getSession();
        if (!session) {
          void authRef.current.reloadSession({ background: true });
          return null;
        }
        return session.csrfToken;
      },
      events: {
        onJoined: (result) => {
          selfConnectionId = result.self.connectionId;
          setConnected(true);
          setMode(result.mode);
          setLesson(result.lesson);
          setSelfLabel(labelFor(result.self));
          assetSync.noteCanonical(result.scene);
          sync.joined(result);
          applyPresence(result.collaborators);
          if (
            !hasEnteredOwnFrame.current &&
            result.self.role === "STUDENT" &&
            result.self.studentNumber !== null
          ) {
            const mine = findMyFrame(
              excalidrawAPI.getSceneElements(),
              result.self.studentNumber,
            );
            if (mine) {
              hasEnteredOwnFrame.current = true;
              excalidrawAPI.scrollToContent(mine, {
                animate: false,
                fitToViewport: false,
              });
            }
          }
          if (
            !hasShownOverview.current &&
            result.self.role === "TEACHER" &&
            !isPresentation
          ) {
            hasShownOverview.current = showOverview(excalidrawAPI);
          }
          setSceneTick((value) => value + 1);
        },
        onJoinFailed: (code) => loseAccess(code),
        onCanonical: (update) => sync.canonical(update),
        onLesson: (state) => setLesson(state),
        onPresence: (snapshot) => applyPresence(snapshot.collaborators),
        onPointer: (update) => {
          const id = update.identity.connectionId as SocketId;
          if (update.identity.connectionId === selfConnectionId) {
            return;
          }
          const next = new Map(collaborators);
          next.set(id, {
            ...collaborators.get(id),
            id,
            socketId: id,
            username: labelFor(update.identity),
            pointer: {
              ...update.pointer,
              // The core already draws a remote laser trail; a teacher's
              // pointer gets that, a student's stays a plain cursor.
              tool: update.identity.role === "TEACHER" ? "laser" : "pointer",
            },
            button: update.button,
            selectedElementIds: update.selectedElementIds as Record<
              string,
              true
            >,
          });
          showCollaborators(next);
        },
        onRevoked: (event) => {
          setConnected(false);
          showCollaborators(new Map());
          sync.disconnected();
          if (event.code === "UNAUTHENTICATED") {
            // A rotated session cookie; rejoin with the new token.
            transport?.reconnect();
            return;
          }
          loseAccess(event.code);
        },
        onDisconnected: () => {
          setConnected(false);
          showCollaborators(new Map());
          sync.disconnected();
        },
        onAccessLost: (code) => loseAccess(code),
      },
    });

    syncRef.current = sync;
    assetRef.current = assetSync;
    transportRef.current = transport;
    transport.connect();
    return () => {
      transport?.close();
      sync.dispose();
      assetSync.dispose();
      syncRef.current = null;
      assetRef.current = null;
      transportRef.current = null;
    };
  }, [
    excalidrawAPI,
    api,
    classId,
    canvasId,
    createTransport,
    initial,
    isTeacher,
    selfNumber,
    isPresentation,
  ]);

  useEffect(() => {
    // Only unacknowledged work warns; nothing claims to be saved before that.
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (
        syncRef.current?.hasUnsavedChanges() ||
        assetRef.current?.hasPendingUploads()
      ) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // Operator probe for a text box the 한/영 key cannot switch (live class,
  // 10-22); reported as a diagnostic, nothing shown to the student.
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    return attachTextInputProbe(document, {
      getActiveTool: () => {
        const { type, locked } = excalidrawAPI.getAppState().activeTool;
        return { type, locked };
      },
      report: (kind, detail) => {
        recordClientEvent("diagnostic", `${kind} ${JSON.stringify(detail)}`);
        transportRef.current?.diagnostic({ canvasId, kind, detail });
      },
    });
  }, [excalidrawAPI, canvasId]);

  useEffect(() => {
    // In-app navigation unmounts the editor, which would drop pending work.
    setLeaveGuard((proceed) => {
      if (
        !(
          syncRef.current?.hasUnsavedChanges() ||
          assetRef.current?.hasPendingUploads()
        )
      ) {
        return true;
      }
      leaveProceed.current = proceed;
      setLeaveOpen(true);
      return false;
    });
    return () => setLeaveGuard(null);
  }, []);

  // The core calls onChange after every render, including the ~50ms pointer
  // broadcasts a 20-student class sends (each lands as `updateScene({
  // collaborators })`). Those leave the scene content untouched, but the
  // *element array reference* is not a safe signal by itself: the core's own
  // drag/resize interactions mutate elements in place (`mutateElement`) and
  // never reallocate the array, so an in-progress drag would look identical
  // too and the frame-guard revert below would never run. `getSceneVersion`
  // (the sum of every element's `.version` — the same "did anything change"
  // signal the original excalidraw-app collab client polled) is what
  // actually guarantees "nothing here changed": every real edit, in place or
  // not, bumps `.version` first. Selection is compared by reference instead:
  // the core always hands a fresh object when the selection itself changes,
  // and never touches it on a collaborator-only update, so this stays cheap
  // without needing its own hash.
  const lastSceneVersion = useRef<number | null>(null);
  const lastSelectedElementIds = useRef<AppState["selectedElementIds"] | null>(
    null,
  );

  const onChange = useCallback(
    (
      elements: readonly OrderedExcalidrawElement[],
      appState: AppState,
      files: BinaryFiles,
    ) => {
      // Before the unchanged-scene guard below: switching tool or cancelling
      // a placement changes no element, yet the cursor and hint follow it.
      const nextPlacing: Placing =
        appState.activeTool.type === "custom" &&
        appState.activeTool.customType === "pixelArt"
          ? "pixel"
          : appState.activeTool.type === "image" &&
            appState.pendingImageElementId
          ? "image"
          : null;
      if (nextPlacing !== placingRef.current) {
        const previous = placingRef.current;
        placingRef.current = nextPlacing;
        setPlacing(nextPlacing);
        if (nextPlacing === "pixel") {
          pixelArmedRef.current = true;
          excalidrawAPI?.setToast({
            message: PIXEL_ART_PLACEMENT_HINT,
            duration: Infinity,
          });
        } else if (previous === "pixel") {
          if (pixelArmedRef.current) {
            // Left the tool without placing (Esc, another tool): cancelled.
            pendingPixelArtRef.current = null;
            pixelArmedRef.current = false;
          }
          if (nextPlacing === "image") {
            // The image hint takes over the toast. Its own block below sits
            // behind the unchanged-scene guard, which this switch may not
            // pass, so show it here and record it as seen.
            pendingImageElementId.current = appState.pendingImageElementId;
            excalidrawAPI?.setToast({
              message: IMAGE_PLACEMENT_HINT,
              duration: Infinity,
            });
          } else {
            excalidrawAPI?.setToast(null);
          }
        }
      }
      if (
        !initialSceneApplied.current &&
        excalidrawAPI &&
        !appState.isLoading
      ) {
        initialSceneApplied.current = true;
        // A join that already arrived filled the scene itself.
        if (elements.length === 0 && initialElements.length > 0) {
          excalidrawAPI.updateScene({
            elements: initialElements,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          if (isTeacher && !isPresentation) {
            hasShownOverview.current = showOverview(excalidrawAPI);
          }
          if (!hasShownOverview.current) {
            excalidrawAPI.scrollToContent(undefined, { animate: false });
          }
          return;
        }
      }
      const sceneVersion = getSceneVersion(elements);
      const unchanged =
        sceneVersion === lastSceneVersion.current &&
        appState.selectedElementIds === lastSelectedElementIds.current;
      lastSceneVersion.current = sceneVersion;
      lastSelectedElementIds.current = appState.selectedElementIds;
      if (unchanged) {
        return;
      }

      // A presentation canvas lays its row out with the slide buttons only,
      // for the teacher too: a dragged frame would leave its slot.
      if ((!isTeacher || isPresentation) && excalidrawAPI) {
        const selection = withoutSelectedFrames(
          elements,
          appState.selectedElementIds,
          appState.selectionElement !== null,
        );
        if (selection) {
          // Re-enters onChange with the frame-free selection.
          excalidrawAPI.updateScene({
            appState: { selectedElementIds: selection },
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          return;
        }
      }

      setSceneTick((value) => value + 1);

      // The core exposes "waiting for a placement click" only through this
      // appState field; there is no signal for the processing that happens
      // before it (resize/hash/decode), which only changes the native
      // cursor. Guarding on core files, we settle for covering the click
      // wait, the part a toast can actually make less confusing.
      if (appState.pendingImageElementId !== pendingImageElementId.current) {
        pendingImageElementId.current = appState.pendingImageElementId;
        excalidrawAPI?.setToast(
          appState.pendingImageElementId
            ? { message: IMAGE_PLACEMENT_HINT, duration: Infinity }
            : null,
        );
      }

      // Everything the teacher creates is teacher material, and whatever
      // the teacher deletes stays deleted: both are closed to students by
      // the shared rule. Computed here but applied below through
      // `updateScene` with `captureUpdate: NEVER`, never by mutating the
      // live elements in place: an in-place mutation never touches the
      // store's own snapshot, so the very next unrelated undoable action
      // folds this tag into *its* diff, and the teacher's next Ctrl+Z on
      // that action strips the tag along with it — reviving a deletion, or
      // handing a student edit rights on live worksheet material.
      if (isTeacher && syncRef.current) {
        const changes = new Map<string, ExcalidrawElement["customData"]>();
        for (const element of elements) {
          if (isFrameLikeElement(element)) {
            continue;
          }
          const scene = element as unknown as SerializedExcalidrawElement;
          const stored = syncRef.current.revisionOf(element.id);
          const marked =
            element.customData?.canvaschool?.deletedByTeacher === true;
          if (!stored) {
            if (!element.isDeleted && !isWorksheetElement(scene)) {
              changes.set(
                element.id,
                withWorksheetTag(element, true).customData,
              );
            }
          } else if (
            element.isDeleted !== stored.isDeleted &&
            element.isDeleted !== marked
          ) {
            // Deleted here by the teacher, or brought back here: only this
            // screen's own change, never a deletion that arrived from a
            // student.
            changes.set(
              element.id,
              withTeacherDeletionMark(element, element.isDeleted).customData,
            );
          }
        }
        // A new element is tagged in place, as the core creates it: the core
        // keeps mutating the object it inserted (a shape being drawn, text
        // being typed, an image being loaded), so swapping it through
        // updateScene lost the drawing — shapes stayed 0×0 and images never
        // finished (e2e). If a later undo strips this tag, the server
        // re-applies it (teacherTagDefense); the deletion mark below has no
        // such in-flight object and stays out of history instead.
        for (const element of elements) {
          const customData = changes.get(element.id);
          if (customData && !syncRef.current.revisionOf(element.id)) {
            mutateElement(element, { customData }, false);
            changes.delete(element.id);
          }
        }
        if (changes.size > 0 && excalidrawAPI) {
          excalidrawAPI.updateScene({
            elements: elements.map((element) =>
              changes.has(element.id)
                ? bumpVersion({
                    ...element,
                    customData: changes.get(element.id),
                  })
                : element,
            ) as readonly OrderedExcalidrawElement[],
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          // The call above re-enters onChange with the tagged elements (a
          // new array, so the guard above won't skip it); that pass finishes
          // asset sync and localChange with the tags already applied.
          // Finishing them here too would send the untagged copy first.
          return;
        }
      }

      if (
        syncRef.current &&
        excalidrawAPI &&
        (isTeacher
          ? settleTeacherEdits(elements, appState, syncRef.current)
          : selfNumber !== null &&
            settleStudentEdits(elements, appState, syncRef.current, selfNumber))
      ) {
        // Re-enters onChange with the settled elements (same objects, newer
        // versions); that pass syncs assets and sends them.
        excalidrawAPI.updateScene({
          elements: [...elements],
          captureUpdate: CaptureUpdateAction.NEVER,
        });
        return;
      }

      assetRef.current?.sync(elements, files);
      // An element still awaiting its placement click is not an edit yet:
      // the core drops a picked image into the scene with frameId null
      // before the student has said where it goes, and the shared student
      // rule would refuse (and delete) it before the click could place it.
      // It is judged once the click assigns its position and frame; its
      // upload above may start early.
      syncRef.current?.localChange(
        toScene(
          appState.pendingImageElementId
            ? elements.filter(
                (element) => element.id !== appState.pendingImageElementId,
              )
            : elements,
        ),
      );
    },
    [excalidrawAPI, isTeacher, initialElements, selfNumber, isPresentation],
  );

  /** The frame the seal button acts on: the student's own, or whichever the
   * teacher has selected. Recomputed on every scene change since the
   * imperative API holds no state a memo could depend on directly. */
  const focusFrame = useMemo(() => {
    const elements = excalidrawAPI?.getSceneElements() ?? [];
    if (selfNumber !== null) {
      return findMyFrame(elements, selfNumber);
    }
    const selected = excalidrawAPI?.getAppState().selectedElementIds ?? {};
    return (
      elements.find(
        (element) => isFrameLikeElement(element) && selected[element.id],
      ) ?? null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excalidrawAPI, selfNumber, sceneTick]);

  /** Comment panes only ever hang under a sealed frame (spec 6.1) — sealing
   * is the "now look at this" signal, and unsealing takes it back. */
  const sealedFrames = useMemo(() => {
    const elements = excalidrawAPI?.getSceneElements() ?? [];
    return elements.filter(
      (element) =>
        isFrameLikeElement(element) && readFrameMeta(element).sealed !== null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excalidrawAPI, sceneTick]);

  /** A student's own 자랑하기 posts on this canvas: read when the canvas
   * opens and after each publish or withdraw — never polled. */
  const [galleryPosts, setGalleryPosts] = useState<ClassGalleryPost[]>([]);
  const isStudent = selfNumber !== null && !isPresentation;
  const loadGalleryPosts = useCallback(() => {
    if (!isStudent) {
      return;
    }
    api
      .listCanvasGalleryPosts(classId, canvasId)
      .then(setGalleryPosts)
      .catch(() => {
        // Without the list the control just offers 자랑하기; the server
        // still decides what is allowed.
      });
  }, [api, classId, canvasId, isStudent]);
  useEffect(() => {
    loadGalleryPosts();
  }, [loadGalleryPosts]);
  /** Newest post per frame wins (a frame can be posted again after 내리기). */
  const galleryPostByFrame = useMemo(() => {
    const byFrame = new Map<string, ClassGalleryPost>();
    for (const post of galleryPosts) {
      const known = byFrame.get(post.frameId);
      if (!known || known.updatedAt < post.updatedAt) {
        byFrame.set(post.frameId, post);
      }
    }
    return byFrame;
  }, [galleryPosts]);

  /** Owned frames are the student slots: a class may use fewer than 30
   * accounts and disabled students leave gaps, so completion tracks the real
   * numbers rather than 1..N. */
  const ownedFrameNumbers = useMemo(
    () =>
      (excalidrawAPI?.getSceneElements() ?? [])
        .flatMap((element) => {
          const owner = isFrameLikeElement(element)
            ? readFrameMeta(element).owner
            : null;
          return owner === null ? [] : [owner];
        })
        .sort((left, right) => left - right),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [excalidrawAPI, sceneTick],
  );
  const hasOwnerTaggedFrames = ownedFrameNumbers.length > 0;

  /** Group frames carry no owner (membership changes every lesson), so
   * there's no "my frame" to bind a header seal button to — anyone opens
   * the group seal modal instead and targets a frame by name. */
  const groupFrames = useMemo(() => {
    if (hasOwnerTaggedFrames) {
      return [];
    }
    return (excalidrawAPI?.getSceneElements() ?? []).filter((element) =>
      isFrameLikeElement(element),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excalidrawAPI, hasOwnerTaggedFrames, sceneTick]);

  /** `updateScene` re-triggers `onChange`, which is how the seal reaches the
   * save path. Every element these shell edits replaced gets a new version,
   * as any Excalidraw edit does — the sync sends only new revisions.
   *
   * `captureUpdate: NEVER`, not `IMMEDIATELY`: these are frame-metadata
   * writes (seal, comments, focus/help mode, frame color) that touch the
   * whole `customData.canvaschool` namespace at once. Making them undoable
   * meant a teacher's *next*, unrelated undo could replay this whole write
   * — restoring the seal/comment/mode state as it was *before* this call and
   * silently wiping out any concurrent change (another student's comment, a
   * mode switch) made after it. `NEVER` still updates the store's snapshot
   * (and still reaches the save path via onChange), it just never becomes a
   * step on the undo stack. */
  const applyElements = useCallback(
    (elements: readonly ExcalidrawElement[]) => {
      if (!excalidrawAPI) {
        return;
      }
      const current = new Set(excalidrawAPI.getSceneElementsIncludingDeleted());
      excalidrawAPI.updateScene({
        elements: elements.map((element) =>
          current.has(element as OrderedExcalidrawElement)
            ? element
            : bumpVersion({ ...element }),
        ) as readonly OrderedExcalidrawElement[],
        captureUpdate: CaptureUpdateAction.NEVER,
      });
    },
    [excalidrawAPI],
  );

  /** Focus mode (집중모드) is the default on an individual canvas; help
   * mode (도움모드) is on only while the teacher has flagged the frames. */
  const isFocusMode = useMemo(() => {
    const frames = (excalidrawAPI?.getSceneElements() ?? []).filter(
      isFrameLikeElement,
    );
    return (
      frames.some((frame) => readFrameMeta(frame).owner !== null) &&
      !frames.some((frame) => readFrameMeta(frame).helpMode)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excalidrawAPI, sceneTick]);

  /** Switches an individual canvas between focus and help mode. Entering
   * focus mode colors the frames and sends every student to their own. */
  const toggleFocusMode = useCallback(() => {
    if (!excalidrawAPI) {
      return;
    }
    if (!hasOwnerTaggedFrames) {
      setNotice(FOCUS_MODE_INDIVIDUAL_ONLY);
      return;
    }
    const allElements = excalidrawAPI.getSceneElementsIncludingDeleted();
    const frames = allElements.filter(
      (element) => isFrameLikeElement(element) && !element.isDeleted,
    );
    const nextFocus = !isFocusMode;
    const now = new Date().toISOString();

    const updated = allElements.map((element) => {
      if (!isFrameLikeElement(element) || element.isDeleted) {
        return element;
      }
      const meta = readFrameMeta(element);
      if (!nextFocus) {
        return writeFrameMeta(
          { ...element, strokeColor: "#bbb", backgroundColor: "transparent" },
          {
            ...meta,
            helpMode: true,
            worksheetLocked: false,
            worksheetLockedAt: null,
            frameColor: null,
            frameBgColor: null,
          },
        );
      }
      const paletteIdx =
        meta.owner !== null ? meta.owner - 1 : frames.indexOf(element);
      const color = FRAME_PALETTE[Math.abs(paletteIdx) % FRAME_PALETTE.length];
      return writeFrameMeta(
        { ...element, strokeColor: color.border, backgroundColor: color.bg },
        {
          ...meta,
          helpMode: false,
          worksheetLocked: true,
          worksheetLockedAt: now,
          frameColor: color.border,
          frameBgColor: color.bg,
        },
      );
    });

    applyElements(updated);
    setNotice(
      nextFocus
        ? "집중모드: 학생은 자기 프레임에만 그릴 수 있습니다. 학생 화면이 각자의 프레임으로 이동합니다."
        : "도움모드: 학생이 친구 프레임도 고칠 수 있습니다.",
    );
  }, [excalidrawAPI, applyElements, hasOwnerTaggedFrames, isFocusMode]);

  const lastWorksheetJump = useRef<string | null>(null);

  useEffect(() => {
    if (!excalidrawAPI || selfNumber === null) {
      return;
    }
    const elements = excalidrawAPI.getSceneElements();
    const frameWithLock = elements.find(
      (element) =>
        isFrameLikeElement(element) &&
        !readFrameMeta(element).helpMode &&
        readFrameMeta(element).worksheetLockedAt,
    );
    const lockedAt = frameWithLock
      ? readFrameMeta(frameWithLock).worksheetLockedAt
      : null;

    if (lockedAt && lockedAt !== lastWorksheetJump.current) {
      lastWorksheetJump.current = lockedAt;
      const mine = findMyFrame(elements, selfNumber);
      if (mine) {
        excalidrawAPI.scrollToContent(mine, {
          animate: true,
          fitToViewport: false,
        });
        const centerX = Math.round(mine.x + mine.width / 2);
        const centerY = Math.round(mine.y + mine.height / 2);
        transportRef.current?.sendPointer({
          canvasId,
          pointer: { x: centerX, y: centerY },
          button: "up",
          selectedElementIds: {},
        });
        setNotice("집중모드입니다. 내 번호의 프레임으로 이동했습니다.");
      }
    }
  }, [excalidrawAPI, selfNumber, sceneTick, canvasId]);

  const toggleCompletionSlot = useCallback(
    (slot: number, checked: boolean) => {
      // Show the check only once it has actually left this tab: a green the
      // server never heard about looks done to the student and stays white
      // for the teacher.
      const sent = transportRef.current?.sendCompletion({
        canvasId,
        slot,
        checked,
      });
      if (!sent) {
        setNotice(
          "연결이 끊겨 완료 표시를 보내지 못했습니다. 잠시 후 다시 눌러 주세요.",
        );
        return;
      }
      setLesson((current) => {
        const already = current.completion.checked.includes(slot);
        if (checked === already) {
          return current;
        }
        const nextChecked = checked
          ? [...current.completion.checked, slot].sort(
              (left, right) => left - right,
            )
          : current.completion.checked.filter((value) => value !== slot);
        return {
          ...current,
          completion: { ...current.completion, checked: nextChecked },
        };
      });
    },
    [canvasId],
  );

  const onPointerUpdate = useMemo(
    () =>
      throttle((payload: PointerPayload) => {
        const transport = transportRef.current;
        if (!transport || !excalidrawAPI || payload.pointersMap.size > 1) {
          return;
        }
        transport.sendPointer({
          canvasId,
          pointer: { x: payload.pointer.x, y: payload.pointer.y },
          button: payload.button,
          selectedElementIds: excalidrawAPI.getAppState().selectedElementIds,
        });
      }, POINTER_INTERVAL_MILLISECONDS),
    [canvasId, excalidrawAPI],
  );
  useEffect(() => () => onPointerUpdate.cancel(), [onPointerUpdate]);

  // Backstop for gestures the capture-phase listeners above can't reach
  // (e.g. touchscreen pinch-zoom), which don't fire wheel/keydown events.
  // Also the only place scroll/zoom reach render, so comment panes — screen
  // UI, not canvas elements — read their position from here.
  const onScrollChange = useCallback(
    (scrollX: number, scrollY: number, zoom: AppState["zoom"]) => {
      setViewport({ scrollX, scrollY, zoom: zoom.value });
      if (zoomLocked && excalidrawAPI && zoom.value !== STANDARD_ZOOM_VALUE) {
        excalidrawAPI.updateScene({
          appState: { zoom: { value: STANDARD_ZOOM_VALUE } },
          captureUpdate: CaptureUpdateAction.NEVER,
        });
      }
    },
    [zoomLocked, excalidrawAPI],
  );

  const coverMode = isPresentation
    ? "NONE"
    : coverModeFor(lesson.lock, selfNumber);
  const covered = coverMode !== "NONE";
  const readOnly = mode === "READ_ONLY" || accessCode !== null || covered;
  readOnlyRef.current = readOnly;

  // A cover or archive ends any pixel art in progress.
  useEffect(() => {
    if (!readOnly) {
      return;
    }
    setPixelModal(null);
    pendingPixelArtRef.current = null;
    pixelArmedRef.current = false;
    if (placingRef.current === "pixel") {
      excalidrawAPI?.setActiveTool({ type: "selection" });
    }
  }, [readOnly, excalidrawAPI]);

  // Keys reach the core only through its focused container, and a closing
  // modal leaves focus on the page body: hand it back.
  const focusCore = useCallback(() => {
    canvasWrapperRef.current
      ?.querySelector<HTMLElement>(".excalidraw")
      ?.focus({ preventScroll: true });
  }, []);

  // For the placement wait (Esc cancels it).
  useEffect(() => {
    if (placing === "pixel") {
      focusCore();
    }
  }, [placing, focusCore]);

  // However the modal closed (cancel, X, Esc, saved), except through a cover:
  // that screen's own blur logic owns focus then.
  const pixelModalWasOpen = useRef(false);
  useEffect(() => {
    if (pixelModal) {
      pixelModalWasOpen.current = true;
    } else if (pixelModalWasOpen.current) {
      pixelModalWasOpen.current = false;
      if (!readOnlyRef.current) {
        focusCore();
      }
    }
  }, [pixelModal, focusCore]);

  const openPixelArtModal = useCallback((state: PixelArtModalState) => {
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      canvasWrapperRef.current?.contains(active)
    ) {
      active.blur();
    }
    setPixelModal(state);
  }, []);

  const onPixelArtToolbarClick = useCallback(() => {
    if (pixelModalRef.current) {
      return;
    }
    if (placingRef.current === "pixel") {
      // Pressed again while waiting for the click: cancel.
      excalidrawAPI?.setActiveTool({ type: "selection" });
      return;
    }
    openPixelArtModal({});
  }, [excalidrawAPI, openPixelArtModal]);

  const savePixelArt = useCallback(
    async (grid: PixelGrid) => {
      const state = pixelModalRef.current;
      const built = await buildPixelArtFile(grid);
      // Closed meanwhile (a cover came down, say): nothing to place.
      if (
        !excalidrawAPI ||
        readOnlyRef.current ||
        pixelModalRef.current !== state
      ) {
        return;
      }
      if (state?.editId) {
        const all = excalidrawAPI.getSceneElementsIncludingDeleted();
        const target = all.find((element) => element.id === state.editId);
        if (
          !target ||
          target.isDeleted ||
          target.locked ||
          (!isTeacher &&
            !studentMayReplacePixelArt(all, selfNumber, target, isPresentation))
        ) {
          setPixelModal(null);
          excalidrawAPI.setToast({
            message: PIXEL_ART_STALE_NOTICE,
            duration: 3_000,
          });
          return;
        }
        excalidrawAPI.addFiles([
          {
            id: built.fileId,
            mimeType: "image/png",
            dataURL: built.dataURL,
            created: Date.now(),
          },
        ]);
        excalidrawAPI.updateScene({
          elements: all.map((element) =>
            element.id === target.id
              ? newElementWith(element, { fileId: built.fileId } as any)
              : element,
          ) as readonly OrderedExcalidrawElement[],
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });
        setPixelModal(null);
        return;
      }
      pendingPixelArtRef.current = built;
      pixelArmedRef.current = false;
      setPixelModal(null);
      excalidrawAPI.setActiveTool({ type: "custom", customType: "pixelArt" });
    },
    [excalidrawAPI, isTeacher, selfNumber, isPresentation],
  );

  const onPixelArtEdit = useCallback(
    async (id: string) => {
      const element = excalidrawAPI
        ?.getSceneElementsIncludingDeleted()
        .find((candidate) => candidate.id === id);
      const file =
        element && isPixelArtImage(element) && element.fileId
          ? excalidrawAPI?.getFiles()[element.fileId]
          : undefined;
      if (!file) {
        return;
      }
      try {
        openPixelArtModal({
          initialGrid: await decodePixelArt(file.dataURL),
          editId: id,
        });
      } catch {
        excalidrawAPI?.setToast({
          message: "그림을 불러오지 못했어요. 다시 눌러 보세요.",
          duration: 3_000,
        });
      }
    },
    [excalidrawAPI, openPixelArtModal],
  );

  const onPixelArtPointerDown = useCallback<
    NonNullable<ExcalidrawProps["onPointerDown"]>
  >(
    (activeTool, pointerDownState) => {
      const pending = pendingPixelArtRef.current;
      if (
        !excalidrawAPI ||
        activeTool.type !== "custom" ||
        activeTool.customType !== "pixelArt" ||
        !pending
      ) {
        return;
      }
      pendingPixelArtRef.current = null;
      pixelArmedRef.current = false;
      if (readOnlyRef.current) {
        excalidrawAPI.setActiveTool({ type: "selection" });
        return;
      }
      const { origin } = pointerDownState;
      excalidrawAPI.addFiles([
        {
          id: pending.fileId,
          mimeType: "image/png",
          dataURL: pending.dataURL,
          created: Date.now(),
        },
      ]);
      let image: ExcalidrawElement = createPixelArtElement(
        pending.fileId,
        origin,
      );
      if (isTeacher) {
        // settleTeacherEdits skips worksheet material, so the frame is
        // assigned here; a student's frame is settled by settleStudentEdits.
        const frames = excalidrawAPI
          .getSceneElements()
          .filter(isFrameLikeElement);
        const frameId =
          frameByPosition(serializedOf(image), frames.map(serializedOf))?.id ??
          null;
        image = newElementWith(image, { frameId });
      }
      excalidrawAPI.updateScene({
        elements: [
          ...excalidrawAPI.getSceneElementsIncludingDeleted(),
          image,
        ] as readonly OrderedExcalidrawElement[],
        appState: { selectedElementIds: { [image.id]: true } },
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });
      excalidrawAPI.setActiveTool({ type: "selection" });
    },
    [excalidrawAPI, isTeacher],
  );

  /** The one selected pixel art the viewer may re-draw, if any. */
  const pixelEdit = useMemo(() => {
    if (!excalidrawAPI || readOnly) {
      return null;
    }
    const selected = excalidrawAPI.getAppState().selectedElementIds;
    const selectedIds = Object.keys(selected).filter((id) => selected[id]);
    if (selectedIds.length !== 1) {
      return null;
    }
    const all = excalidrawAPI.getSceneElementsIncludingDeleted();
    const element = all.find((candidate) => candidate.id === selectedIds[0]);
    if (
      !element ||
      element.isDeleted ||
      element.locked ||
      !isPixelArtImage(element) ||
      !element.fileId ||
      !excalidrawAPI.getFiles()[element.fileId] ||
      (!isTeacher &&
        !studentMayReplacePixelArt(all, selfNumber, element, isPresentation))
    ) {
      return null;
    }
    return { id: element.id, anchor: rotatedRightEdge(element) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    excalidrawAPI,
    readOnly,
    sceneTick,
    selfNumber,
    isTeacher,
    isPresentation,
  ]);

  // ---- Presentation canvas: slides, completion, hand-over to the show ----
  const [hoveredFrameId, setHoveredFrameId] = useState<string | null>(null);
  const [slideDelete, setSlideDelete] = useState<string | null>(null);
  const [completedAt, setCompletedAt] = useState(canvas.completedAt);
  const [completionPending, setCompletionPending] = useState(false);
  const [students, setStudents] = useState<readonly PresentationStudent[]>([]);
  const [movingStudent, setMovingStudent] = useState(false);
  // The teacher stepping on from a slideshow lands in the next student's
  // slideshow, not in a canvas to start over from.
  const [showMode, setShowMode] = useState<"editor" | "fetch" | null>(() =>
    readAutoShow(canvas.id) ? "fetch" : null,
  );

  const sceneEditable = isPresentation && !readOnly;

  const onPointerMove = useCallback(
    (payload: PointerPayload) => {
      if (isPresentation && excalidrawAPI) {
        const { x, y } = payload.pointer;
        const margin =
          SLIDE_HOVER_MARGIN_PX / excalidrawAPI.getAppState().zoom.value;
        const hit = slideFrames(excalidrawAPI.getSceneElements()).find(
          (frame) =>
            x >= frame.x &&
            x <= frame.x + frame.width &&
            y >= frame.y - margin &&
            y <= frame.y + frame.height,
        );
        setHoveredFrameId(hit?.id ?? null);
      }
      onPointerUpdate(payload);
    },
    [isPresentation, excalidrawAPI, onPointerUpdate],
  );

  const slides = useMemo(
    () =>
      isPresentation && excalidrawAPI
        ? slideFrames(excalidrawAPI.getSceneElements())
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPresentation, excalidrawAPI, sceneTick],
  );
  /** Under the pointer, else the slide holding the selected drawing. */
  const activeSlideId = useMemo(() => {
    if (hoveredFrameId && slides.some((frame) => frame.id === hoveredFrameId)) {
      return hoveredFrameId;
    }
    if (!excalidrawAPI) {
      return null;
    }
    const selected = excalidrawAPI.getAppState().selectedElementIds;
    const element = excalidrawAPI
      .getSceneElements()
      .find(
        (candidate) => selected[candidate.id] && !isFrameLikeElement(candidate),
      );
    return element
      ? frameByPosition(serializedOf(element), slides.map(serializedOf))?.id ??
          null
      : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredFrameId, slides, excalidrawAPI, sceneTick]);

  /** One edit of the row, in one `updateScene` and out of the undo history
   * (the reverse button undoes it). */
  const applySlideEdit = useCallback(
    (elements: readonly ExcalidrawElement[]) => {
      excalidrawAPI?.updateScene({
        elements: elements as readonly OrderedExcalidrawElement[],
        captureUpdate: CaptureUpdateAction.NEVER,
      });
    },
    [excalidrawAPI],
  );
  const followSlide = useCallback(
    (frame: ExcalidrawElement) =>
      excalidrawAPI?.scrollToContent(frame, {
        animate: true,
        fitToViewport: false,
      }),
    [excalidrawAPI],
  );
  const onAddSlide = useCallback(() => {
    if (!excalidrawAPI || !sceneEditable) {
      return;
    }
    const edit = addSlide(excalidrawAPI.getSceneElementsIncludingDeleted());
    if (edit) {
      applySlideEdit(edit.elements);
      followSlide(edit.frame);
    }
  }, [excalidrawAPI, sceneEditable, applySlideEdit, followSlide]);
  const onSwapSlide = useCallback(
    (frameId: string, direction: -1 | 1) => {
      if (!excalidrawAPI || !sceneEditable) {
        return;
      }
      const edit = swapSlide(
        excalidrawAPI.getSceneElementsIncludingDeleted(),
        frameId,
        direction,
      );
      if (edit) {
        applySlideEdit(edit.elements);
        followSlide(edit.frame);
      }
    },
    [excalidrawAPI, sceneEditable, applySlideEdit, followSlide],
  );
  const askDeleteSlide = useCallback((frameId: string) => {
    // Keys reach the core through its focused container; a dialog over it
    // must not leave Delete and the arrows live underneath.
    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      active.blur();
    }
    setSlideDelete(frameId);
  }, []);
  const confirmDeleteSlide = () => {
    const frameId = slideDelete;
    setSlideDelete(null);
    if (!excalidrawAPI || !sceneEditable || frameId === null) {
      return;
    }
    const edited = deleteSlide(
      excalidrawAPI.getSceneElementsIncludingDeleted(),
      frameId,
    );
    if (edited) {
      setHoveredFrameId(null);
      applySlideEdit(edited);
    }
    focusCore();
  };

  // A presentation canvas starts with one empty slide, "1". The server makes
  // the scene empty on purpose; the student's own editor lays the frame, so a
  // teacher merely looking at a canvas not yet started does not change it.
  useEffect(() => {
    if (
      isTeacher ||
      !sceneEditable ||
      !excalidrawAPI ||
      !connected ||
      mode !== "EDIT"
    ) {
      return;
    }
    if (!initialSceneApplied.current) {
      return;
    }
    const edit = ensureFirstSlide(
      excalidrawAPI.getSceneElementsIncludingDeleted(),
      route.canvasId,
    );
    if (edit) {
      applySlideEdit(edit.elements);
      excalidrawAPI.scrollToContent(edit.frame, {
        animate: false,
        fitToViewport: false,
      });
    }
  }, [
    isTeacher,
    sceneEditable,
    excalidrawAPI,
    connected,
    mode,
    sceneTick,
    applySlideEdit,
    route.canvasId,
  ]);

  const toggleCompletion = useCallback(async () => {
    setCompletionPending(true);
    try {
      const updated = await api.setCanvasCompletion(
        classId,
        canvasId,
        completedAt === null,
      );
      setCompletedAt(updated.completedAt);
    } catch {
      setNotice("완료 표시를 바꾸지 못했어요. 잠시 후 다시 눌러 주세요.");
    } finally {
      setCompletionPending(false);
    }
  }, [api, classId, canvasId, completedAt]);

  // Teacher: the lesson's students, to step from one canvas to the next.
  const lessonId = canvas.parentCanvasId;
  useEffect(() => {
    if (!isTeacher || !isPresentation || lessonId === null) {
      return;
    }
    let cancelled = false;
    api
      .listPresentationStudents(classId, lessonId)
      .then((list) => {
        if (!cancelled) {
          setStudents(list.items);
        }
      })
      .catch(() => {
        // Without the list the step buttons stay off.
      });
    return () => {
      cancelled = true;
    };
  }, [api, classId, lessonId, isTeacher, isPresentation]);
  const ownNumber = canvas.ownerStudentNumber;
  const { previousStudent, nextStudent } = useMemo(() => {
    const enabled = students
      .filter((entry) => entry.enabled)
      .sort((left, right) => left.studentNumber - right.studentNumber);
    return {
      previousStudent:
        ownNumber === null
          ? null
          : [...enabled]
              .reverse()
              .find((entry) => entry.studentNumber < ownNumber) ?? null,
      nextStudent:
        ownNumber === null
          ? null
          : enabled.find((entry) => entry.studentNumber > ownNumber) ?? null,
    };
  }, [students, ownNumber]);
  const openStudent = useCallback(
    async (target: PresentationStudent, thenShow: boolean) => {
      if (lessonId === null) {
        return;
      }
      setMovingStudent(true);
      try {
        // An existing canvas is used as listed: only creating one needs the
        // POST, which an archived class refuses.
        const openedId =
          target.canvasId ??
          (
            await api.openPresentationStudentCanvas(
              classId,
              lessonId,
              target.studentNumber,
            )
          ).id;
        if (thenShow) {
          writeAutoShow(openedId);
        }
        navigate(canvasPath(classId, openedId));
      } catch {
        setNotice("학생 캔버스를 열지 못했어요. 잠시 후 다시 눌러 주세요.");
      } finally {
        setMovingStudent(false);
      }
    },
    [api, classId, lessonId, navigate],
  );

  openShowRef.current = () => {
    if (!excalidrawAPI) {
      return;
    }
    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      active.blur();
    }
    setShowScene({
      elements: excalidrawAPI.getSceneElements(),
      files: excalidrawAPI.getFiles(),
    });
    setShowMode("editor");
  };
  const closeShow = useCallback(() => {
    setShowMode(null);
    setShowScene(null);
    focusCore();
  }, [focusCore]);

  const groupSlots = useMemo(
    () => Array.from({ length: lesson.groupCount }, (_, index) => index + 1),
    [lesson.groupCount],
  );
  // The canvas's template fixes the mode: owner-tagged frames mean an
  // individual template (student numbers), anything else counts groups. The
  // server's stored `completion.mode` is ignored, so there is no toggle.
  const completion: Completion = {
    ...lesson.completion,
    mode: hasOwnerTaggedFrames ? "INDIVIDUAL" : "GROUP",
  };
  const slots = hasOwnerTaggedFrames ? ownedFrameNumbers : groupSlots;
  const allChecked =
    slots.length > 0 &&
    slots.every((slot) => lesson.completion.checked.includes(slot));
  const wasAllChecked = useRef(false);
  // The class settings tab owns the actual on/off control and writes this
  // same key; a canvas open in another tab only needs to pick it up, so a
  // one-time read on mount plus a `storage` listener (cheap, and this is
  // the one place two tabs commonly disagree) beats polling.
  const [chimeMuted, setChimeMutedState] = useState(
    () => window.localStorage.getItem(chimeMutedStorageKey(classId)) === "true",
  );
  useEffect(() => {
    setChimeMuted(chimeMuted);
  }, [chimeMuted]);
  useEffect(() => {
    const key = chimeMutedStorageKey(classId);
    const onStorage = (event: StorageEvent) => {
      if (event.key === key) {
        setChimeMutedState(event.newValue === "true");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [classId]);
  // Closed by default: a full-class roster rendered inline used to grow the
  // teacher bar past one line, and this is a "check in on" panel rather
  // than something read on every glance (spec: teacher bar one-line fix).
  const [rosterOpen, setRosterOpen] = useState(false);
  const [groupSealOpen, setGroupSealOpen] = useState(false);
  const [pdfExportOpen, setPdfExportOpen] = useState(false);
  // The record is already loaded, so a quick export has the file name; a
  // presentation student's canvas is not in the class list to look it up.
  const canvasTitle = canvas.title;
  const [openCommentFrameId, setOpenCommentFrameId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    // Only the teacher hears it: thirty tablets chiming at once is a riot,
    // and children press the button just to make the noise. The rule is
    // just "everyone just became checked" — no dedupe beyond that; a
    // teacher clearing a check and the class refilling it rings again.
    if (isTeacher && allChecked && !wasAllChecked.current) {
      playChime();
    }
    wasAllChecked.current = allChecked;
  }, [isTeacher, allChecked]);

  useEffect(() => {
    // A stale tab that never reports covered is the teacher's only clue
    // that a student's screen didn't actually go behind the cover.
    transportRef.current?.sendPresence({
      canvasId,
      state: "ACTIVE",
      handRaised,
      covered,
      raisedAt,
    });
  }, [canvasId, covered, handRaised, raisedAt]);

  /** Always (re-)raises — there is no separate "lower" affordance, so every
   * click stamps a fresh moment and restarts the ten-minute clock below. */
  const raiseHand = useCallback(() => {
    setHandRaised(true);
    setRaisedAt(new Date().toISOString());
  }, []);

  useEffect(() => {
    if (!handRaised || raisedAt === null) {
      return;
    }
    // Absolute-timestamp timeout, same device as `AuthProvider`'s session
    // refresh: the delay is derived from when the hand went up, not a bare
    // `setTimeout(10 * 60 * 1000)`, so re-raising resets the clock exactly
    // once (a fresh effect run) instead of stacking timers.
    const targetTime = Date.parse(raisedAt) + HAND_RAISE_TIMEOUT_MILLISECONDS;
    const delay = Math.min(
      MAX_TIMER_DELAY_MILLISECONDS,
      Math.max(0, targetTime - Date.now()),
    );
    const handle = window.setTimeout(() => {
      setHandRaised(false);
      setRaisedAt(null);
    }, delay);
    return () => window.clearTimeout(handle);
  }, [handRaised, raisedAt]);

  useEffect(() => {
    if (!covered) {
      return;
    }
    // Keyboard shortcuts (arrows, Delete) reach Excalidraw only while its
    // container holds focus; a student who clicked the canvas before the
    // cover appeared would otherwise keep editing underneath it.
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      canvasWrapperRef.current?.contains(active)
    ) {
      active.blur();
    }
  }, [covered]);

  // Read-only debug hook for the end-to-end chaos harness, which drives the
  // real production build with mouse coordinates and needs a way to ask the
  // editor what it currently believes without touching the DOM. Absent
  // unless explicitly opted into (never present for a student or teacher in
  // an ordinary lesson).
  useEffect(() => {
    if (
      !excalidrawAPI ||
      window.localStorage.getItem("canvaschool:debug") !== "1"
    ) {
      return;
    }
    const historyStats = (): { undo: number; redo: number } | null => {
      // The public API exposes only `history.clear()`; the real History
      // instance (with its undo/redo stacks) is reachable only through the
      // test-only `window.h` hook, which does not exist in a production
      // build. There, this always resolves to null, as documented.
      try {
        const testHook = (
          window as unknown as {
            h?: { history?: { undoStack?: unknown[]; redoStack?: unknown[] } };
          }
        ).h;
        const undo = testHook?.history?.undoStack?.length;
        const redo = testHook?.history?.redoStack?.length;
        return typeof undo === "number" && typeof redo === "number"
          ? { undo, redo }
          : null;
      } catch {
        return null;
      }
    };
    const debugHook = {
      canvasId,
      version: () => syncRef.current?.debug().version ?? null,
      epoch: () => syncRef.current?.debug().epoch ?? null,
      status: () => syncRef.current?.status() ?? null,
      pending: () =>
        syncRef.current?.debug().pending ?? {
          buffer: 0,
          splitQueue: 0,
          inFlight: 0,
          heldBack: 0,
        },
      knownDigest: () =>
        syncRef.current?.debug().knownDigest() ?? Promise.resolve(null),
      sceneElements: () =>
        JSON.parse(
          JSON.stringify(excalidrawAPI.getSceneElementsIncludingDeleted()),
        ),
      historyStats,
      verifyNow: () =>
        syncRef.current?.debug().verifyNow() ??
        Promise.resolve({ error: "not ready" }),
      viewport: () => {
        const appState = excalidrawAPI.getAppState();
        return {
          scrollX: appState.scrollX,
          scrollY: appState.scrollY,
          zoom: appState.zoom.value,
          offsetLeft: appState.offsetLeft,
          offsetTop: appState.offsetTop,
          width: appState.width,
          height: appState.height,
        };
      },
      // Same transform `SealedFrameBadge`'s `toScreen` uses in the other
      // direction (`screenX = (sceneX + scrollX) * zoom`), solved for the
      // scroll that centers the given scene point; zoom untouched unless the
      // caller (the filming camera) asks for one.
      scrollToScenePoint: (x: number, y: number, zoom?: number) => {
        const appState = excalidrawAPI.getAppState();
        const value = (zoom ?? appState.zoom.value) as NormalizedZoomValue;
        excalidrawAPI.updateScene({
          appState: {
            scrollX: appState.width / 2 / value - x,
            scrollY: appState.height / 2 / value - y,
            zoom: { value },
          },
          captureUpdate: CaptureUpdateAction.NEVER,
        });
      },
      selectedIds: () => {
        const selected = excalidrawAPI.getAppState().selectedElementIds;
        return Object.keys(selected).filter((id) => selected[id]);
      },
    };
    (
      window as unknown as { __canvaschoolDebug?: typeof debugHook }
    ).__canvaschoolDebug = debugHook;
    return () => {
      delete (window as unknown as { __canvaschoolDebug?: typeof debugHook })
        .__canvaschoolDebug;
    };
  }, [excalidrawAPI, canvasId]);

  const statusLabel = (() => {
    if (accessCode) {
      return null;
    }
    if (mode === "READ_ONLY") {
      return "읽기 전용";
    }
    if (!connected) {
      return saveStatus === "saved"
        ? "연결 중…"
        : "연결이 끊겼습니다. 다시 연결하는 중…";
    }
    if (saveStatus === "retrying") {
      return "저장하지 못했습니다. 다시 시도하는 중…";
    }
    return saveStatus === "saving" || assets.uploading > 0
      ? "저장 중…"
      : "저장됨";
  })();

  const saveDot =
    statusLabel === "저장됨"
      ? "canvaschool-save-dot canvaschool-save-dot--saved"
      : statusLabel === "연결이 끊겼습니다. 다시 연결하는 중…" ||
        statusLabel === "저장하지 못했습니다. 다시 시도하는 중…"
      ? "canvaschool-save-dot canvaschool-save-dot--danger"
      : "canvaschool-save-dot";

  return (
    <div className="canvaschool-editor">
      {leaveOpen && (
        <Modal
          title="저장하지 않고 나가기"
          primaryLabel="나가기"
          secondaryLabel="계속 편집"
          danger
          onCancel={() => {
            setLeaveOpen(false);
            leaveProceed.current = null;
          }}
          onPrimary={() => {
            const proceed = leaveProceed.current;
            leaveProceed.current = null;
            setLeaveOpen(false);
            proceed?.();
          }}
        >
          <p>{LEAVE_WARNING}</p>
        </Modal>
      )}
      {frameDelete && (
        <Modal
          title="프레임 지우기"
          primaryLabel="지우기"
          secondaryLabel="취소"
          danger
          onCancel={() => answerFrameDelete(false)}
          onPrimary={() => answerFrameDelete(true)}
        >
          <p>
            {frameDelete.frames.length === 1
              ? `‘${frameDelete.frames[0].name ?? "프레임"}’ 프레임을`
              : `프레임 ${frameDelete.frames.length}개를`}{" "}
            지울까요? 프레임이 없어지면 그 칸의 학생은 더 이상 그 안에 그릴 수
            없습니다. 안에 있던 내용은 지워지지 않고 프레임 밖으로 빠집니다.
          </p>
          <p>실수로 지웠다면 바로 Ctrl+Z(실행 취소)로 되돌릴 수 있습니다.</p>
        </Modal>
      )}
      {slideDelete !== null && (
        <Modal
          title="슬라이드 지우기"
          primaryLabel="지우기"
          secondaryLabel="취소"
          danger
          onCancel={() => {
            setSlideDelete(null);
            focusCore();
          }}
          onPrimary={confirmDeleteSlide}
        >
          <p>
            {slides.findIndex((frame) => frame.id === slideDelete) + 1}번
            슬라이드와 그 안의 그림을 지울까요? 뒤에 있는 슬라이드는 한 칸씩
            앞으로 옵니다.
          </p>
        </Modal>
      )}
      {showMode !== null && (
        <PresentationShow
          classId={classId}
          canvasId={canvasId}
          scene={showMode === "editor" ? showScene ?? undefined : undefined}
          onExit={closeShow}
          next={
            isTeacher && nextStudent
              ? {
                  label: `다음 학생: ${nextStudent.studentNumber}번${
                    nextStudent.displayName ? ` ${nextStudent.displayName}` : ""
                  }`,
                  onNext: () => {
                    closeShow();
                    void openStudent(nextStudent, true);
                  },
                }
              : undefined
          }
        />
      )}
      <div className="canvaschool-editor-stage">
        <div
          ref={canvasWrapperRef}
          className={[
            "canvaschool-editor-canvas",
            zoomLocked && "canvaschool-zoom-locked",
            placing && `canvaschool-editor-canvas--placing-${placing}`,
          ]
            .filter(Boolean)
            .join(" ")}
          style={
            placing
              ? ({
                  "--canvaschool-cursor":
                    placing === "pixel"
                      ? PIXEL_ART_PLACE_CURSOR
                      : IMAGE_PLACE_CURSOR,
                } as React.CSSProperties)
              : undefined
          }
        >
          <Excalidraw
            excalidrawAPI={setExcalidrawAPI}
            initialData={initialData}
            onChange={onChange}
            onPointerDown={onPixelArtPointerDown}
            onPointerUpdate={onPointerMove}
            onScrollChange={onScrollChange}
            isCollaborating
            viewModeEnabled={readOnly}
            langCode={langCode}
            UIOptions={uiOptions}
            confirmFrameDelete={confirmFrameDelete}
            pasteTextAsSingleElement
            renderTopRightUI={renderTopRightUI}
          >
            {isTeacher && (
              // Same entries as Excalidraw's fallback menu minus the file
              // items, plus the PDF export.
              <MainMenu>
                <MainMenu.Item
                  icon={<FileDown size={16} aria-hidden="true" />}
                  onSelect={() => setPdfExportOpen(true)}
                  data-testid="pdf-export-button"
                >
                  PDF로 내보내기
                </MainMenu.Item>
                <MainMenu.DefaultItems.SearchMenu />
                <MainMenu.DefaultItems.Help />
                <MainMenu.DefaultItems.ClearCanvas />
                <MainMenu.Separator />
                <MainMenu.Group title="Excalidraw links">
                  <MainMenu.DefaultItems.Socials />
                </MainMenu.Group>
                <MainMenu.Separator />
                <MainMenu.DefaultItems.ToggleTheme />
                <MainMenu.DefaultItems.ChangeCanvasBackground />
              </MainMenu>
            )}
            <Footer>
              {/* Every lesson control sits in Excalidraw's own footer row,
                  immediately right of the zoom and undo/redo islands. Going
                  through `Footer` rather than placing the dock ourselves is
                  what keeps it there: the row lays itself out, so the dock
                  still starts right after those islands when the zoom one is
                  hidden (students draw at a fixed zoom). Being inside
                  Excalidraw's subtree also puts it under the cover overlay,
                  so a covered student can't reach it. */}
              <div
                className="canvaschool-editor-dock"
                // Any click here primes the chime: the browser only unblocks
                // audio after a user gesture on the page, and this is the one
                // place that gesture is guaranteed to happen before class starts.
                onClickCapture={isTeacher ? primeChime : undefined}
                onPointerDown={(event) => event.stopPropagation()}
              >
                {isTeacher && !isPresentation && (
                  <div className="canvaschool-teacher-controls">
                    <div className="canvaschool-roster-toggle">
                      <button
                        type="button"
                        aria-expanded={rosterOpen}
                        onClick={() => setRosterOpen((open) => !open)}
                      >
                        참여 현황
                        {(() => {
                          const unconfirmed = countUnconfirmed(
                            roster,
                            lesson.lock.mode !== "OFF",
                          );
                          return unconfirmed > 0 ? ` (${unconfirmed})` : "";
                        })()}
                      </button>
                      {rosterOpen && (
                        <RosterPanel
                          members={roster}
                          coveredExpected={lesson.lock.mode !== "OFF"}
                        />
                      )}
                    </div>
                    <TeacherBar
                      lesson={lesson}
                      onLock={(lockMode, text) =>
                        transportRef.current?.sendLock({
                          canvasId,
                          mode: lockMode,
                          message: text,
                          durationSeconds: null,
                          exemptStudentNumbers:
                            lesson.lock.exemptStudentNumbers,
                        })
                      }
                      onUnlock={() =>
                        transportRef.current?.sendLock({
                          canvasId,
                          mode: "OFF",
                          message: "",
                          durationSeconds: null,
                          exemptStudentNumbers: [],
                        })
                      }
                      onToggleFocusMode={toggleFocusMode}
                      focusMode={isFocusMode}
                      focusModeAvailable={hasOwnerTaggedFrames}
                    />
                  </div>
                )}
                {hasOwnerTaggedFrames && focusFrame && (
                  <SealButton
                    sealed={readFrameMeta(focusFrame).sealed !== null}
                    canSeal={selfNumber !== null}
                    canUnseal={
                      isTeacher ||
                      readFrameMeta(focusFrame).owner === selfNumber
                    }
                    onSeal={() =>
                      applyElements(
                        sealFrame(
                          excalidrawAPI?.getSceneElementsIncludingDeleted() ??
                            [],
                          focusFrame.id,
                          selfNumber,
                        ),
                      )
                    }
                    onUnseal={() =>
                      applyElements(
                        unsealFrame(
                          excalidrawAPI?.getSceneElementsIncludingDeleted() ??
                            [],
                          focusFrame.id,
                        ),
                      )
                    }
                  />
                )}
                {hasOwnerTaggedFrames &&
                  focusFrame &&
                  readFrameMeta(focusFrame).sealed === null &&
                  (isTeacher ||
                    readFrameMeta(focusFrame).owner === selfNumber) && (
                    <FrameBackgroundButton
                      background={readFrameMeta(focusFrame).background ?? null}
                      onChange={(background) =>
                        applyElements(
                          setFrameBackground(
                            excalidrawAPI?.getSceneElementsIncludingDeleted() ??
                              [],
                            focusFrame.id,
                            background,
                          ),
                        )
                      }
                    />
                  )}
                {!isPresentation &&
                  !hasOwnerTaggedFrames &&
                  groupFrames.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setGroupSealOpen(true)}
                    >
                      모둠 마감
                    </button>
                  )}
                {selfNumber !== null && !isPresentation && (
                  <div className="canvaschool-dock-actions">
                    {focusFrame && (
                      <button
                        type="button"
                        onClick={() =>
                          excalidrawAPI?.scrollToContent(focusFrame, {
                            animate: false,
                            fitToViewport: false,
                          })
                        }
                      >
                        내 프레임 찾아가기
                      </button>
                    )}
                    <button
                      type="button"
                      aria-pressed={handRaised}
                      className={
                        handRaised
                          ? "canvaschool-hand-raise canvaschool-hand-raise--active"
                          : "canvaschool-hand-raise"
                      }
                      onClick={raiseHand}
                    >
                      <Hand size={18} aria-hidden="true" />
                      {handRaised ? "손들었어요!" : "손들기"}
                    </button>
                  </div>
                )}
              </div>
            </Footer>
          </Excalidraw>
          {/* There is no editor header any more; the canvas runs to the top
              of the window so Excalidraw's own toolbar sits where that bar
              used to be. The logo keeps its corner, and styles.scss pushes
              Excalidraw's hamburger right by exactly this much so the two
              read as one row. */}
          <Link
            to={classPath(classId)}
            className="canvaschool-editor-logo"
            aria-label="학급으로 돌아가기"
          >
            <Logo size={22} />
          </Link>
          {isPresentation && isTeacher && lessonId !== null && (
            <nav
              className="canvaschool-presentation-nav"
              aria-label="발표 수업 학생 이동"
              onPointerDown={(event) => event.stopPropagation()}
            >
              <Link
                to={canvasPath(classId, lessonId)}
                className="canvaschool-presentation-nav-back"
              >
                <ArrowLeft size={16} aria-hidden="true" />
                발표 수업으로 돌아가기
              </Link>
              <span
                className="canvaschool-presentation-nav-divider"
                aria-hidden="true"
              />
              <button
                type="button"
                disabled={previousStudent === null || movingStudent}
                onClick={() =>
                  previousStudent && void openStudent(previousStudent, false)
                }
              >
                <ChevronLeft size={16} aria-hidden="true" />
                이전 학생
              </button>
              <button
                type="button"
                disabled={nextStudent === null || movingStudent}
                onClick={() =>
                  nextStudent && void openStudent(nextStudent, false)
                }
              >
                다음 학생
                <ChevronRight size={16} aria-hidden="true" />
              </button>
            </nav>
          )}
          {statusLabel && (
            <span className="canvaschool-save-status" aria-live="polite">
              <span className={saveDot} />
              {statusLabel}
            </span>
          )}
          {/* Banners only ever appear when something went wrong, so they
              float over the canvas instead of holding a row open for the
              whole lesson. */}
          <div className="canvaschool-editor-toasts">
            {accessCode && <ErrorAlert code={accessCode} />}
            {notice && (
              <p className="canvaschool-error" role="alert">
                <span>{notice}</span>{" "}
                <button type="button" onClick={() => setNotice(null)}>
                  닫기
                </button>
              </p>
            )}
            {surveyDue && !feedback && (
              <p className="canvaschool-survey" role="status">
                <span>크루바스를 쓰면서 불편한 점이 있었나요?</span>{" "}
                <button
                  type="button"
                  onClick={() => {
                    dismissSurvey();
                    setFeedback("SURVEY");
                  }}
                >
                  알려주기
                </button>{" "}
                <button type="button" onClick={dismissSurvey}>
                  없어요
                </button>
              </p>
            )}
            {assets.failedUploads > 0 && (
              <p className="canvaschool-error" role="alert">
                <span>이미지를 저장하지 못했습니다.</span>{" "}
                <button type="button" onClick={() => assetRef.current?.retry()}>
                  이미지 다시 저장
                </button>
              </p>
            )}
            {assets.failedDownloads > 0 && (
              <p className="canvaschool-error" role="alert">
                <span>이미지를 불러오지 못했습니다.</span>{" "}
                <button type="button" onClick={() => assetRef.current?.retry()}>
                  이미지 다시 불러오기
                </button>
              </p>
            )}
          </div>
          {feedback && (
            <FeedbackModal
              source={feedback}
              api={api}
              extraDetail={() => ({
                saveStatus,
                connected,
                version: syncRef.current?.debug().version ?? null,
                pending: syncRef.current?.debug().pending ?? null,
              })}
              onClose={closeFeedback}
            />
          )}
          {pdfExportOpen && excalidrawAPI && (
            <FramePdfExportModal
              api={excalidrawAPI}
              canvasName={canvasTitle}
              onClose={() => setPdfExportOpen(false)}
            />
          )}
          {groupSealOpen && (
            <GroupSealModal
              frames={groupFrames}
              onToggle={(frameId, sealed) =>
                applyElements(
                  sealed
                    ? unsealFrame(
                        excalidrawAPI?.getSceneElementsIncludingDeleted() ?? [],
                        frameId,
                      )
                    : sealFrame(
                        excalidrawAPI?.getSceneElementsIncludingDeleted() ?? [],
                        frameId,
                        selfNumber,
                      ),
                )
              }
              onClose={() => setGroupSealOpen(false)}
            />
          )}
          {!isPresentation && (
            <CoverOverlay lock={lesson.lock} studentNumber={selfNumber} />
          )}
          {isTeacher && !isPresentation && <HandRaiseTray roster={roster} />}
          {(isPresentation ? [] : sealedFrames).map((sealedFrame) => (
            <SealedFrameBadge
              key={sealedFrame.id}
              frame={sealedFrame}
              commentCount={readFrameMeta(sealedFrame).comments.length}
              toScreen={(x, y) => ({
                x: (x + viewport.scrollX) * viewport.zoom,
                y: (y + viewport.scrollY) * viewport.zoom,
              })}
              zoom={viewport.zoom}
              onOpenComments={() => setOpenCommentFrameId(sealedFrame.id)}
            >
              {/* Only a student's own sealed frame can go outside the school;
                  group frames carry no owner and so never match. */}
              {excalidrawAPI &&
                selfNumber !== null &&
                isFrameLikeElement(sealedFrame) &&
                readFrameMeta(sealedFrame).owner === selfNumber && (
                  <GalleryShowoffControl
                    api={api}
                    classId={classId}
                    canvasId={canvasId}
                    excalidrawAPI={excalidrawAPI}
                    frame={sealedFrame}
                    status={
                      galleryPostByFrame.get(sealedFrame.id)?.status ?? null
                    }
                    postId={galleryPostByFrame.get(sealedFrame.id)?.id ?? null}
                    onChanged={loadGalleryPosts}
                  />
                )}
            </SealedFrameBadge>
          ))}
          {excalidrawAPI && !readOnly && (
            <PixelArtToolbarButton
              wrapper={canvasWrapperRef.current}
              active={pixelModal !== null || placing === "pixel"}
              onClick={onPixelArtToolbarClick}
            />
          )}
          {pixelEdit && (
            <PixelArtEditButton
              anchor={pixelEdit.anchor}
              toScreen={(x, y) => ({
                x: (x + viewport.scrollX) * viewport.zoom,
                y: (y + viewport.scrollY) * viewport.zoom,
              })}
              onEdit={() => void onPixelArtEdit(pixelEdit.id)}
            />
          )}
          {isPresentation && excalidrawAPI && !readOnly && (
            <PresentationSlideControls
              frames={slides}
              activeFrameId={activeSlideId}
              toScreen={(x, y) => {
                // Read from the core: `viewport` lags until its first scroll.
                const view = excalidrawAPI.getAppState();
                return {
                  x: (x + view.scrollX) * view.zoom.value,
                  y: (y + view.scrollY) * view.zoom.value,
                };
              }}
              zoom={excalidrawAPI.getAppState().zoom.value}
              canAdd={canAddSlide(excalidrawAPI.getSceneElements())}
              onAdd={onAddSlide}
              onSwap={onSwapSlide}
              onDelete={askDeleteSlide}
            />
          )}
          {pixelModal && (
            <PixelArtModal
              initialGrid={pixelModal.initialGrid}
              onSave={savePixelArt}
              onClose={() => setPixelModal(null)}
            />
          )}
          {openCommentFrameId &&
            (() => {
              const openFrame = sealedFrames.find(
                (sealedFrame) => sealedFrame.id === openCommentFrameId,
              );
              // The teacher may unseal the frame while a student still has
              // its modal open — nothing left to show, so close quietly.
              if (!openFrame) {
                return null;
              }
              return (
                <CommentModal
                  frame={openFrame}
                  comments={readFrameMeta(openFrame).comments}
                  // Sealing is the gate now: a frame only takes comments
                  // once it is sealed, which is why the teacher's old
                  // lesson-wide 학생 댓글 열기 switch is gone.
                  // Individual canvases too: a student may comment on a
                  // friend's sealed frame (spec 2026-09-16 §6.1); edit and
                  // delete stay limited to their own via canManageComment.
                  canWrite={isTeacher || selfNumber !== null}
                  onAdd={(text) =>
                    applyElements(
                      addComment(
                        excalidrawAPI?.getSceneElementsIncludingDeleted() ?? [],
                        openFrame.id,
                        {
                          id: createUuid(),
                          at: new Date().toISOString(),
                          authorLabel: selfLabel,
                          authorRole: isTeacher ? "TEACHER" : "STUDENT",
                          authorNumber: isTeacher ? null : selfNumber,
                          text,
                        },
                      ),
                    )
                  }
                  canManage={(comment) =>
                    canManageComment(comment, {
                      isTeacher,
                      number: selfNumber,
                      label: selfLabel,
                    })
                  }
                  onEdit={(commentId, text) =>
                    applyElements(
                      editComment(
                        excalidrawAPI?.getSceneElementsIncludingDeleted() ?? [],
                        openFrame.id,
                        commentId,
                        text,
                      ),
                    )
                  }
                  onRemove={(commentId) =>
                    applyElements(
                      removeComment(
                        excalidrawAPI?.getSceneElementsIncludingDeleted() ?? [],
                        openFrame.id,
                        commentId,
                      ),
                    )
                  }
                  onClose={() => setOpenCommentFrameId(null)}
                />
              );
            })()}
        </div>
        {isPresentation ? (
          <PresentationCompletion
            completed={completedAt !== null}
            pending={completionPending}
            onToggle={isTeacher ? undefined : () => void toggleCompletion()}
          />
        ) : (
          <CompletionPanel
            completion={completion}
            slots={slots}
            allChecked={allChecked}
            onToggle={toggleCompletionSlot}
            isTeacher={isTeacher}
            onReset={() =>
              transportRef.current?.sendCompletionReset({ canvasId })
            }
          />
        )}
      </div>
    </div>
  );
};

type LoadState =
  | { status: "loading" }
  | { status: "failed"; code: ApiErrorCode }
  | {
      status: "ready";
      canvasId: string;
      state: CanvasState;
      canvas: CanvasRecord;
    };

/** Loads the canonical canvas over HTTP, then opens the live editor. */
export const CanvasEditor = ({
  route,
  canvas: givenCanvas,
  createTransport = createCollaborationTransport,
}: CanvasEditorProps) => {
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;
  const { api } = auth;
  const [load, setLoad] = useState<LoadState>({ status: "loading" });

  const loadState = useCallback(async () => {
    setLoad({ status: "loading" });
    try {
      // The record says what kind of canvas this is, and so which rules and
      // controls the editor wears.
      const known = givenCanvas?.id === route.canvasId ? givenCanvas : null;
      const [state, canvas] = await Promise.all([
        api.getCanvasState(route.classId, route.canvasId),
        known ?? api.getCanvas(route.classId, route.canvasId),
      ]);
      setLoad({ status: "ready", canvasId: route.canvasId, state, canvas });
    } catch (error) {
      setLoad({ status: "failed", code: authRef.current.handleError(error) });
    }
  }, [api, givenCanvas, route.classId, route.canvasId]);

  useEffect(() => {
    void loadState();
  }, [loadState]);

  // Going from one canvas to another (a teacher stepping through students)
  // must not render the new route over the old canvas's data.
  if (
    load.status === "loading" ||
    (load.status === "ready" && load.canvasId !== route.canvasId)
  ) {
    return <p aria-busy="true">캔버스를 불러오는 중…</p>;
  }
  if (load.status === "failed") {
    return (
      <>
        <ErrorAlert code={load.code} />
        <button type="button" onClick={() => void loadState()}>
          다시 시도
        </button>{" "}
        <Link to={classPath(route.classId)}>학급으로 돌아가기</Link>
      </>
    );
  }
  return (
    <EditorSurface
      key={route.canvasId}
      route={route}
      initial={load.state}
      canvas={load.canvas}
      createTransport={createTransport}
    />
  );
};
